<template>
  <div
    class="messenger-contract-page"
    :class="{ 'messenger-contract-page--embedded': embedded }"
  >
    <section v-if="!embedded" class="contract-hero">
      <div>
        <div class="hero-eyebrow">งาน Messenger</div>
        <h1 class="hero-title">สัญญา Messenger B2B</h1>
        <p class="hero-subtitle">
          จัดการแพ็กเกจ Messenger แบบสัญญารายรอบสำหรับทุกจังหวัดที่ให้บริการ
        </p>
      </div>
    </section>

    <v-card
      class="contract-shell glass-card"
      :class="{
        'contract-shell--embedded': embedded,
        'section-card--embedded': embedded,
      }"
    >
      <v-toolbar flat color="transparent" class="contract-toolbar">
        <div class="toolbar-copy">
          <div class="toolbar-title">
            {{ "รายการสัญญา" }}
          </div>
          <div class="toolbar-subtitle">
            {{ contracts.length }} สัญญาใน
            {{ selectedProvinceName || "จังหวัดที่เลือก" }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedProvinceSlug"
          :items="provinceOptions"
          item-text="text"
          item-value="value"
          label="จังหวัด"
          outline
          dense
          hide-details
          class="province-filter"
          :loading="loadingProvinces"
          :disabled="provinceLocked"
          :prepend-inner-icon="provinceLocked ? 'lock' : undefined"
          @change="changeProvince"
        ></v-select>
        <v-select
          v-model="filters.status"
          :items="statusFilterItems"
          label="สถานะ"
          outline
          dense
          hide-details
          class="status-filter"
          @change="fetchContracts"
        ></v-select>
        <v-tooltip top>
          <v-btn
            icon
            slot="activator"
            color="error"
            class="toolbar-icon-btn"
            aria-label="ประวัติสัญญาที่ลบ"
            :loading="loadingDeleted"
            @click="openDeletedHistory"
          >
            <v-icon>history</v-icon>
          </v-btn>
          <span>ประวัติสัญญาที่ลบ</span>
        </v-tooltip>

        <v-btn
          icon
          color="primary"
          class="black--text toolbar-icon-btn"
          aria-label="รีเฟรชรายการสัญญา"
          :loading="loading"
          @click="fetchContracts"
        >
          <v-icon>refresh</v-icon>
        </v-btn>

        <v-btn
          color="success"
          round
          dark
          class="create-contract-btn"
          @click="openCreate"
        >
          <v-icon left>add</v-icon>
          สร้างสัญญาใหม่
        </v-btn>
      </v-toolbar>

      <v-layout row wrap class="summary-row">
        <v-flex
          xs6
          sm6
          md3
          v-for="item in summaryCards"
          :key="item.label"
          class="pa-1"
        >
          <div class="summary-item">
            <v-icon :color="item.color">{{ item.icon }}</v-icon>
            <div>
              <div class="summary-value">{{ item.value }}</div>
              <div class="summary-label">{{ item.label }}</div>
            </div>
          </div>
        </v-flex>
      </v-layout>

      <v-data-table
        :headers="headers"
        :items="contracts"
        :loading="loading"
        :pagination.sync="pagination"
        class="contract-table"
      >
        <template slot="items" slot-scope="props">
          <td data-label="สัญญา">
            <div>{{ props.item.contract_code || props.item.contract_id }}</div>
            <div v-if="props.item.delete_at" class="muted-text">
              ลบเมื่อ {{ formatDateTime(props.item.delete_at) }}
            </div>
          </td>
          <td data-label="เจ้าของสัญญา">
            <div class="strong-text">
              {{ props.item.owner_full_name || "-" }}
            </div>
            <div class="muted-text">
              รหัสผู้ใช้ {{ props.item.owner_user_id || "-" }}
            </div>
          </td>
          <td data-label="ชื่อสัญญา">{{ props.item.package_name || "-" }}</td>
          <td data-label="สถานะ">
            <v-chip
              small
              :color="statusColor(props.item.status)"
              text-color="white"
            >
              {{ contractStatusLabel(props.item.status) }}
            </v-chip>
          </td>
          <td data-label="ราคาต่อรอบ" class="text-xs-right">
            {{
              money(props.item.normal_price_per_round || props.item.total_price)
            }}
          </td>
          <td data-label="รอบที่ใช้แล้ว" class="text-xs-center">
            {{ props.item.used_rounds || 0 }}/{{ props.item.total_rounds || 0 }}
          </td>
          <td data-label="วันที่เริ่ม">
            {{ formatDateTime(props.item.schedule_start_at) }}
          </td>
          <td
            data-label="จัดการ"
            class="contract-row-actions justify-center layout px-0"
          >
            <v-tooltip top>
              <v-btn
                icon
                slot="activator"
                :loading="openingContractId === props.item.contract_id"
                @click="selectContract(props.item)"
              >
                <v-icon small>edit</v-icon>
              </v-btn>
              <span>เปิด</span>
            </v-tooltip>
            <v-tooltip top>
              <v-btn
                icon
                slot="activator"
                :disabled="!!props.item.delete_at"
                :loading="duplicatingContractId === props.item.contract_id"
                @click.stop="duplicateContract(props.item)"
              >
                <v-icon small>file_copy</v-icon>
              </v-btn>
              <span>ทำสำเนาเป็นฉบับร่าง</span>
            </v-tooltip>
            <v-tooltip v-if="!props.item.delete_at" top>
              <v-btn
                icon
                slot="activator"
                color="error"
                :loading="deletingContractId === props.item.contract_id"
                @click.stop="deleteContract(props.item)"
              >
                <v-icon small>delete</v-icon>
              </v-btn>
              <span>ลบสัญญา</span>
            </v-tooltip>
          </td>
        </template>
        <template slot="no-data">
          <div class="pa-4 text-xs-center">
            <span v-if="!loading">ไม่พบสัญญา</span>
            <span v-else>กำลังโหลดสัญญา...</span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      persistent
      v-model="dialog"
      max-width="1480"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
      content-class="contract-dialog"
    >
      <v-card class="dialog-glass-card contract-detail-card">
        <v-toolbar dark color="rgba(24, 28, 36, .92)" class="dialog-toolbar">
          <v-toolbar-title>
            {{
              isEditing
                ? "สัญญา " + (form.contract_code || form.contract_id)
                : "สร้างสัญญา Messenger"
            }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="contract-dialog-content">
          <v-container fluid grid-list-lg class="dialog-body">
            <v-layout row wrap>
              <v-flex xs12 md8>
                <v-card class="section-card glass-section">
                  <v-card-title primary-title>
                    <div>
                      <h3 class="section-title">ข้อมูลสัญญา</h3>
                      <div class="muted-text">
                        เจ้าของสัญญา รายละเอียด รถ และบริการเสริม
                      </div>
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex xs12>
                        <v-radio-group
                          v-model="ownerMode"
                          row
                          hide-details
                          class="owner-mode"
                          :disabled="!canEditPriceInputs"
                          @change="changeOwnerMode"
                        >
                          <v-radio
                            label="สร้างให้ตนเอง"
                            value="me"
                            color="primary"
                          ></v-radio>
                          <v-radio
                            label="สร้างให้ลูกค้า"
                            value="customer"
                            color="primary"
                          ></v-radio>
                          <v-radio
                            label="สร้างให้บริษัท"
                            value="company"
                            color="primary"
                          ></v-radio>
                        </v-radio-group>
                      </v-flex>
                      <v-flex v-if="ownerMode === 'customer'" xs12 sm8>
                        <v-autocomplete
                          v-model="selectedOwnerCustomer"
                          :items="customerOptions"
                          :search-input.sync="ownerSearch"
                          :loading="searchingCustomers"
                          item-text="display_text"
                          item-value="user_id"
                          label="ค้นหาชื่อลูกค้าหรือเบอร์โทรศัพท์"
                          return-object
                          clearable
                          prepend-icon="person"
                          no-filter
                          :disabled="!canEditPriceInputs"
                          :no-data-text="ownerSearchHint"
                          @change="selectOwnerCustomer"
                        ></v-autocomplete>
                      </v-flex>
                      <v-flex v-else-if="ownerMode === 'company'" xs12 sm8>
                        <v-autocomplete
                          v-model="selectedOwnerCompany"
                          :items="companyOptions"
                          :search-input.sync="companySearch"
                          :loading="searchingCompanies"
                          item-text="display_text"
                          item-value="company_provider_wesmart_id"
                          label="ค้นหาชื่อบริษัท เบอร์โทรศัพท์ หรืออีเมล"
                          return-object
                          clearable
                          no-filter
                          prepend-icon="business"
                          :disabled="!canEditPriceInputs"
                          :no-data-text="companySearchHint"
                          @change="selectOwnerCompany"
                        ></v-autocomplete>
                      </v-flex>
                      <v-flex v-else xs12 sm8>
                        <v-text-field
                          :value="currentUserDisplay"
                          label="เจ้าของสัญญา"
                          prepend-icon="person"
                          readonly
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-text-field
                          :value="ownerMode === 'company' ? 'AR' : 'COD'"
                          label="รูปแบบการชำระเงินของออเดอร์"
                          disabled
                          readonly
                        ></v-text-field>
                      </v-flex>
                      <v-flex
                        v-if="ownerMode === 'company' && form.owner_company_id"
                        xs12
                      >
                        <v-alert
                          :value="true"
                          :type="
                            form.credit_summary &&
                            Number(form.credit_summary.current_balance) < 0
                              ? 'error'
                              : form.credit_summary &&
                                Number(form.credit_summary.current_balance) <
                                  Number(form.normal_price_per_round || 0)
                              ? 'warning'
                              : 'info'
                          "
                        >
                          <div class="company-credit-summary">
                            <div class="company-credit-summary__text">
                              <template v-if="form.credit_summary">
                                เครดิตบริษัท:
                                {{ money(form.credit_summary.current_balance) }}
                                คงเหลือ,
                                {{
                                  money(
                                    form.credit_summary.consumed_by_contract
                                  )
                                }}
                                ใช้ไปกับสัญญานี้
                                <span
                                  v-if="
                                    Number(
                                      form.credit_summary.current_balance
                                    ) < Number(form.normal_price_per_round || 0)
                                  "
                                >
                                  — เครดิตต่ำหรือติดลบเป็นข้อมูลแจ้งเตือน
                                  <br />
                                  ระบบยังคงสร้างออเดอร์ตามสัญญา
                                </span>
                              </template>
                              <template v-else-if="loadingCompanyCredit">
                                กำลังโหลดเครดิตบริษัท...
                              </template>
                              <template v-else>
                                ไม่สามารถแสดงเครดิตบริษัทได้
                              </template>
                            </div>
                            <v-tooltip top>
                              <v-btn
                                slot="activator"
                                icon
                                small
                                class="company-credit-summary__refresh"
                                :loading="loadingCompanyCredit"
                                @click.stop="fetchCompanyCredit(true)"
                              >
                                <v-icon color="white">refresh</v-icon>
                              </v-btn>
                              <span>รีเฟรชเครดิตบริษัท</span>
                            </v-tooltip>
                          </div>
                        </v-alert>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-select
                          v-model.number="form.vehicle_id"
                          :items="vehicleOptions"
                          item-text="text"
                          item-value="value"
                          item-disabled="disabled"
                          label="ประเภทรถ"
                          :loading="loadingVehicles"
                          :disabled="!canEditPriceInputs"
                          @change="changeVehicle"
                        ></v-select>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <v-text-field
                          v-model="form.package_name"
                          label="ชื่อสัญญา"
                          :disabled="!canEditContractDetails"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <v-text-field
                          v-model="form.package_note"
                          label="หมายเหตุสัญญา"
                          :disabled="!canEditContractDetails"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-text-field
                          v-model.number="form.order_create_before_minutes"
                          label="สร้างออเดอร์ล่วงหน้า (นาที)"
                          type="number"
                          min="0"
                          :disabled="!canEditContractDetails"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-select
                          v-model="form.addon_ids"
                          :items="addonOptions"
                          item-text="text"
                          item-value="value"
                          label="บริการเสริม"
                          multiple
                          chips
                          deletable-chips
                          :loading="loadingAddons"
                          :disabled="!form.vehicle_id || !canEditPriceInputs"
                        ></v-select>
                        <div class="muted-text addon-price-summary">
                          รวมค่าบริการเสริม: {{ money(selectedAddonPrice) }}
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>

                <v-card class="section-card glass-section">
                  <v-card-title>
                    <div>
                      <h3 class="section-title">จุดรับ-ส่ง</h3>
                      <div class="muted-text">
                        เพิ่มแล้ว {{ routePoints.length }}/20 จุด
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                  </v-card-title>
                  <v-card-text>
                    <div v-if="canEditPriceInputs" class="route-builder">
                      <v-autocomplete
                        v-model="selectedSavedPointId"
                        :items="savedPointOptions"
                        item-text="display_text"
                        item-value="saved_point_id"
                        :label="
                          ownerMode === 'company'
                            ? 'เลือกจุดของบริษัท'
                            : 'เลือกจุดที่บันทึกไว้'
                        "
                        prepend-icon="place"
                        clearable
                        outline
                        hide-details
                        :loading="loadingSavedPoints"
                        :disabled="
                          !savedPointOwnerParams() || routePoints.length >= 20
                        "
                        :no-data-text="savedPointHint"
                        @keyup.enter="appendSavedPoint"
                      ></v-autocomplete>
                      <v-btn
                        round
                        color="primary"
                        class="black--text contract-form-btn route-builder__add"
                        :disabled="
                          !selectedSavedPointId || routePoints.length >= 20
                        "
                        @click="appendSavedPoint"
                      >
                        <v-icon left>add_location</v-icon>
                        เพิ่มในเส้นทาง
                      </v-btn>
                      <v-btn
                        round
                        color="primary"
                        class="black--text contract-form-btn route-builder__custom"
                        :disabled="routePoints.length >= 20"
                        @click="addStop"
                      >
                        <v-icon left>edit_location</v-icon>
                        เพิ่มจุดใหม่
                      </v-btn>
                    </div>
                    <v-alert
                      v-if="routePoints.length < 2"
                      type="info"
                      outline
                      :value="true"
                      class="route-requirement"
                    >
                      กรุณาเพิ่มอีก {{ 2 - routePoints.length }} จุด
                      เพื่อให้เส้นทางมีอย่างน้อย 2 จุด
                    </v-alert>
                    <v-alert
                      v-if="routePoints.length > 20"
                      type="error"
                      :value="true"
                    >
                      เส้นทางมีได้สูงสุด 20 จุด
                    </v-alert>
                    <div class="route-stop-list">
                      <div
                        v-for="(stop, stopIndex) in routePoints"
                        :key="'route-stop-' + stopIndex"
                        class="route-stop-card"
                      >
                        <div class="route-stop-card__header">
                          <div>
                            <strong> จุดที่ {{ stopIndex + 1 }} </strong>
                            <div v-if="stop.to" class="muted-text">
                              {{ stop.to }}
                            </div>
                          </div>
                          <v-btn
                            v-if="
                              ownerMode === 'company' &&
                              !isCompanySavedPoint(stop)
                            "
                            small
                            color="primary"
                            :loading="savingPointIndex === stopIndex"
                            :disabled="!canSaveCompanyPoint(stop)"
                            @click="saveCompanyPoint(stop, stopIndex)"
                            style="color: black"
                          >
                            <v-icon small left color="black">bookmark</v-icon>
                            บันทึกเป็นจุดของบริษัท
                          </v-btn>
                          <v-chip
                            v-else-if="
                              stop.saved_point_id &&
                              !(
                                ownerMode !== 'company' &&
                                stop.saved_point_owner_mode === 'company'
                              )
                            "
                            small
                            color="green"
                            text-color="white"
                          >
                            <v-icon small left>bookmark</v-icon>
                            {{
                              ownerMode === "company"
                                ? "จุดของบริษัท"
                                : "จุดที่บันทึกไว้"
                            }}
                          </v-chip>
                          <v-btn
                            icon
                            small
                            :disabled="!canEditPriceInputs"
                            @click="removeStop(stopIndex)"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                        </div>
                        <v-text-field
                          v-model="stop.saved_point_name"
                          label="ชื่อจุด"
                          prepend-icon="bookmark_border"
                          :disabled="!canEditPriceInputs"
                        ></v-text-field>
                        <v-text-field
                          v-model="stop.address"
                          label="ที่อยู่"
                          :disabled="!canEditPriceInputs"
                        ></v-text-field>
                        <v-layout row wrap>
                          <v-flex xs12 sm6>
                            <v-text-field
                              v-model="stop.to"
                              label="ชื่อผู้ติดต่อ"
                              :disabled="!canEditPriceInputs"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 sm6>
                            <v-text-field
                              v-model="stop.tel"
                              label="เบอร์โทรศัพท์"
                              type="tel"
                              :disabled="!canEditPriceInputs"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                        <v-text-field
                          v-model="stop.remark"
                          label="หมายเหตุ"
                          :disabled="!canEditPriceInputs"
                        ></v-text-field>
                        <v-layout row wrap>
                          <v-flex xs12 sm6>
                            <v-text-field
                              v-model.number="stop.lat"
                              label="ละติจูด"
                              type="number"
                              :disabled="!canEditPriceInputs"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 sm6>
                            <v-text-field
                              v-model.number="stop.lng"
                              label="ลองจิจูด"
                              type="number"
                              :disabled="!canEditPriceInputs"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <v-card class="section-card glass-section">
                  <v-card-title class="rounds-section-title">
                    <div>
                      <h3 class="section-title">รอบการจัดส่ง</h3>
                      <div class="muted-text">
                        จำนวนรอบและวันที่ของสัญญาคำนวณจากตารางด้านล่าง
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-tooltip top>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        color="primary"
                        class="black--text"
                        :disabled="
                          !isEditing || isDeleted || form.status !== 'active'
                        "
                        :loading="saving"
                        @click="refreshProgress(form)"
                      >
                        <v-icon>sync</v-icon>
                      </v-btn>
                      <span>รีเฟรชความคืบหน้า</span>
                    </v-tooltip>
                  </v-card-title>
                  <v-card-text>
                    <v-layout row wrap class="round-summary-fields">
                      <v-flex xs12 sm4>
                        <v-text-field
                          :value="derivedRoundCount"
                          label="จำนวนรอบทั้งหมด"
                          prepend-icon="repeat"
                          disabled
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-text-field
                          :value="formatDateTime(derivedScheduleStartAt) || '-'"
                          label="เริ่มสัญญา"
                          prepend-icon="event"
                          disabled
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-text-field
                          :value="formatDateTime(derivedScheduleEndAt) || '-'"
                          label="สิ้นสุดสัญญา"
                          prepend-icon="event_available"
                          disabled
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                    <v-layout row wrap v-if="canEditSchedule">
                      <v-flex xs12 sm4>
                        <v-text-field
                          v-model.number="roundGenerator.count"
                          label="จำนวนรอบ"
                          type="number"
                          min="1"
                          max="500"
                          hint="สูงสุด 500 รอบ"
                          persistent-hint
                          :disabled="lockedByOrders"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <v-text-field
                          v-model.number="roundGenerator.interval_minutes"
                          label="ระยะห่างแต่ละรอบ (นาที)"
                          type="number"
                          min="1"
                          :disabled="lockedByOrders"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <contract-date-time-picker
                          v-model="roundGenerator.first_scheduled_at"
                          label="วันและเวลาของรอบแรก"
                          :disabled="lockedByOrders"
                        ></contract-date-time-picker>
                      </v-flex>
                      <v-flex xs12 sm2>
                        <v-btn
                          small
                          round
                          color="primary"
                          class="black--text contract-form-btn"
                          :disabled="
                            lockedByOrders ||
                            Number(roundGenerator.count || 0) < 1 ||
                            Number(roundGenerator.count || 0) > 500
                          "
                          @click="generateRounds"
                        >
                          <v-icon left>date_range</v-icon>
                          สร้างรอบ
                        </v-btn>
                      </v-flex>
                      <v-flex xs12 sm2>
                        <v-btn
                          small
                          round
                          color="primary"
                          class="black--text contract-form-btn"
                          :disabled="derivedRoundCount >= 500"
                          @click="addRound"
                        >
                          <v-icon left>add</v-icon>
                          เพิ่มรอบ
                        </v-btn></v-flex
                      >
                    </v-layout>
                    <v-data-table
                      :headers="roundHeaders"
                      :items="form.rounds"
                      :pagination.sync="roundPagination"
                      class="round-table"
                      :rows-per-page-items="[20, 50, 100]"
                    >
                      <template slot="items" slot-scope="props">
                        <td class="text-xs-center">
                          {{ props.item.round_number }}
                        </td>
                        <td>
                          <contract-date-time-picker
                            v-model="props.item.scheduled_at"
                            label="กำหนดส่ง"
                            :hide-details="true"
                            :disabled="
                              !canEditSchedule || roundLocked(props.item)
                            "
                            class="round-schedule-picker"
                          ></contract-date-time-picker>
                        </td>
                        <td>
                          {{ formatDateTime(props.item.order_create_at) }}
                        </td>
                        <td>
                          <v-chip
                            small
                            :color="roundStatusColor(props.item.status)"
                            text-color="white"
                          >
                            {{ roundStatusLabel(props.item.status) }}
                          </v-chip>
                        </td>
                        <td>
                          {{ props.item.messenger_order_code || "-" }}
                        </td>
                        <td class="text-xs-right">
                          {{ money(props.item.required_credit_amount || 0) }}
                        </td>
                        <td class="text-xs-center">
                          <v-tooltip top>
                            <v-btn
                              icon
                              slot="activator"
                              :disabled="!canCreateOrder(props.item)"
                              :loading="creatingRoundId === props.item.round_id"
                              @click="createOrderNow(props.item)"
                            >
                              <v-icon small>send</v-icon>
                            </v-btn>
                            <span>สร้างออเดอร์ตอนนี้</span>
                          </v-tooltip>
                          <v-btn
                            v-if="canEditSchedule && !roundLocked(props.item)"
                            icon
                            @click="removeRound(props.index)"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                        </td>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
                <v-card
                  v-if="
                    form.credit_transactions && form.credit_transactions.length
                  "
                  class="section-card glass-section mt-3"
                >
                  <v-card-title
                    ><h3 class="section-title">
                      ประวัติรายการเครดิตบริษัท
                    </h3></v-card-title
                  >
                  <v-card-text>
                    <v-data-table
                      :headers="creditTransactionHeaders"
                      :items="form.credit_transactions"
                      :pagination.sync="creditTransactionPagination"
                    >
                      <template slot="items" slot-scope="props">
                        <td>{{ formatDateTime(props.item.create_at) }}</td>
                        <td>
                          {{
                            creditTransactionTypeLabel(
                              props.item.transaction_type
                            )
                          }}
                        </td>
                        <td class="text-xs-right">
                          {{ money(props.item.balance_before) }}
                        </td>
                        <td
                          class="text-xs-right"
                          :style="{
                            color:
                              money(props.item.balance_after) -
                                money(props.item.balance_before) >
                              0
                                ? 'green'
                                : money(props.item.balance_after) -
                                    money(props.item.balance_before) <
                                  0
                                ? 'red'
                                : '',
                          }"
                        >
                          <span
                            v-if="
                              money(props.item.balance_after) -
                                money(props.item.balance_before) >
                              0
                            "
                            >+</span
                          >
                          <span
                            v-else-if="
                              money(props.item.balance_after) -
                                money(props.item.balance_before) <
                              0
                            "
                            >-</span
                          >
                          {{ money(props.item.amount) }}
                        </td>
                        <td class="text-xs-right">
                          {{ money(props.item.balance_after) }}
                        </td>
                        <td>
                          {{
                            props.item.messenger_order_code ||
                            props.item.note ||
                            "-"
                          }}
                        </td>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-flex>

              <v-flex xs12 md4>
                <v-card class="section-card sticky-panel glass-section">
                  <v-card-title>
                    <h3 class="section-title">สรุปราคาและการดำเนินการ</h3>
                  </v-card-title>
                  <v-card-text>
                    <v-alert :value="lockedByOrders" type="info" outline>
                      มีออเดอร์ในสัญญาแล้ว
                      จึงไม่สามารถแก้ไขเส้นทางและข้อมูลราคาได้
                    </v-alert>
                    <v-alert
                      :value="form.status === 'active'"
                      type="info"
                      outline
                    >
                      กรุณาพักสัญญาก่อนแก้ไขรายละเอียดหรือรอบในอนาคต
                    </v-alert>
                    <v-alert :value="isDeleted" type="warning" outline>
                      สัญญานี้ถูกลบเมื่อ {{ formatDateTime(form.delete_at) }}
                      และเปิดดูได้อย่างเดียว
                    </v-alert>
                    <v-alert :value="priceSnapshotDirty" type="warning" outline>
                      ข้อมูลที่มีผลต่อราคาเปลี่ยนแปลง
                      กรุณาคำนวณราคาใหม่ก่อนเปิดใช้งานสัญญา
                    </v-alert>
                    <v-alert :value="hasUnsavedChanges" type="info" outline>
                      กรุณาบันทึกการเปลี่ยนแปลงก่อนเปิดใช้งานสัญญา
                    </v-alert>
                    <v-alert :value="hasOverdueRounds" type="warning" outline>
                      บางรอบเลยเวลาสร้างออเดอร์แล้ว
                      กรุณาแก้ไขกำหนดเวลาก่อนเปิดใช้งานสัญญา
                    </v-alert>
                    <v-alert
                      :value="!!form.price_snapshot_token"
                      type="info"
                      outline
                    >
                      คำนวณราคาแล้ว กรุณาบันทึกการเปลี่ยนแปลงก่อนเปิดใช้งานสัญญา
                    </v-alert>
                    <div v-if="!hasPriceSnapshot" class="snapshot-empty-state">
                      <v-icon color="blue-grey" size="42">calculate</v-icon>
                      <div class="snapshot-empty-state__title">
                        ยังไม่มีข้อมูลราคา
                      </div>
                      <div class="muted-text">
                        เลือกรถและจุดรับ-ส่งให้ครบแล้วจึงคำนวณเส้นทาง
                      </div>
                    </div>
                    <template v-else>
                      <div class="price-grid">
                        <div>
                          <div class="muted-text">ราคารวมต่อรอบ</div>
                          <div class="price-text">
                            {{
                              money(
                                form.normal_price_per_round || form.total_price
                              )
                            }}
                          </div>
                        </div>
                        <div>
                          <div class="muted-text">ค่าจัดส่ง</div>
                          <div class="strong-text">
                            {{
                              money(
                                form.transfer_price_snapshot ||
                                  form.transfer_price
                              )
                            }}
                          </div>
                        </div>
                        <div>
                          <div class="muted-text">ค่าจุดเพิ่มเติม</div>
                          <div class="strong-text">
                            {{
                              money(
                                form.extra_point_price_snapshot ||
                                  form.extra_point_price
                              )
                            }}
                          </div>
                        </div>
                        <div>
                          <div class="muted-text">ค่าบริการเสริม</div>
                          <div class="strong-text">
                            {{
                              money(
                                form.addon_price_snapshot || selectedAddonPrice
                              )
                            }}
                          </div>
                        </div>
                        <div>
                          <div class="muted-text">ค่ารอเพิ่มเติม</div>
                          <div class="strong-text">
                            {{ money(form.extra_time_price_snapshot) }}
                          </div>
                        </div>
                        <div>
                          <div class="muted-text">ระยะทาง</div>
                          <div class="strong-text">{{ distanceText }}</div>
                        </div>
                      </div>
                      <v-divider class="my-3"></v-divider>
                      <div class="progress-line">
                        <span>ใช้ไปแล้ว</span>
                        <strong>{{ form.used_rounds || 0 }}</strong>
                      </div>
                      <div class="progress-line">
                        <span>สำเร็จแล้ว</span>
                        <strong>{{ form.completed_rounds || 0 }}</strong>
                      </div>
                      <div class="progress-line">
                        <span>คงเหลือ</span>
                        <strong>{{
                          form.remaining_rounds || remainingRounds
                        }}</strong>
                      </div>
                      <v-divider class="my-3"></v-divider>
                      <div class="muted-text">คำนวณราคาจาก Google เมื่อ</div>
                      <div class="strong-text mb-2">
                        {{
                          formatDateTime(form.google_price_calculated_at) || "-"
                        }}
                      </div>
                      <div class="muted-text">ลำดับจุดในเส้นทาง</div>
                      <div class="strong-text mb-3">{{ waypointText }}</div>
                    </template>
                    <v-divider class="my-3"></v-divider>
                    <v-btn
                      block
                      round
                      color="primary"
                      class="black--text contract-form-btn contract-calculate-btn"
                      :disabled="!canCalculate"
                      :loading="calculatingSnapshot"
                      @click="calculateSnapshot"
                    >
                      <v-icon left>calculate</v-icon>
                      {{ hasPriceSnapshot ? "คำนวณราคาใหม่" : "คำนวณเส้นทาง" }}
                    </v-btn>
                    <v-btn
                      v-if="
                        !isEditing ||
                        form.status === 'draft' ||
                        form.status === 'paused'
                      "
                      block
                      round
                      outline
                      color="blue-grey"
                      class="contract-form-btn contract-draft-btn"
                      :disabled="
                        isDeleted || (hasPriceSnapshot && priceSnapshotDirty)
                      "
                      :loading="saving"
                      @click="
                        isEditing ? saveContract(false) : createContract(false)
                      "
                    >
                      <v-icon left>save</v-icon>
                      {{
                        form.status === "paused"
                          ? "บันทึกการแก้ไข"
                          : "บันทึกฉบับร่าง"
                      }}
                    </v-btn>
                    <div class="contract-status-actions">
                      <v-btn
                        round
                        color="success"
                        class="contract-form-btn"
                        aria-label="เปิดใช้งานสัญญา"
                        :disabled="!canActivate"
                        :loading="saving"
                        @click="setStatus('active')"
                      >
                        <v-icon :left="$vuetify.breakpoint.smAndUp">
                          play_arrow
                        </v-icon>
                        <span v-if="$vuetify.breakpoint.smAndUp"
                          >เปิดใช้งาน</span
                        >
                      </v-btn>
                      <v-btn
                        round
                        color="warning"
                        class="contract-form-btn"
                        aria-label="พักสัญญา"
                        :disabled="
                          !isEditing || form.status !== 'active' || isDeleted
                        "
                        :loading="saving"
                        @click="setStatus('paused')"
                      >
                        <v-icon :left="$vuetify.breakpoint.smAndUp"
                          >pause</v-icon
                        >
                        <span v-if="$vuetify.breakpoint.smAndUp">พักสัญญา</span>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      persistent
      v-model="deletedDialog"
      max-width="1180px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
      content-class="deleted-contract-dialog"
    >
      <v-card class="dialog-glass-card deleted-contract-card">
        <v-toolbar dark color="rgba(24, 28, 36, .92)" class="dialog-toolbar">
          <v-toolbar-title>ประวัติสัญญาที่ลบ</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            dark
            :loading="loadingDeleted"
            @click="fetchDeletedContracts"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
          <v-btn icon dark @click="deletedDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-data-table
            :headers="deletedHeaders"
            :items="deletedContracts"
            :loading="loadingDeleted"
            :pagination.sync="deletedPagination"
            class="contract-table deleted-contract-table"
          >
            <template slot="items" slot-scope="props">
              <td data-label="สัญญา">
                {{ props.item.contract_code || props.item.contract_id }}
              </td>
              <td data-label="เจ้าของสัญญา">
                <div class="strong-text">
                  {{ props.item.owner_full_name || "-" }}
                </div>
                <div class="muted-text">
                  ID {{ props.item.owner_user_id || "-" }}
                </div>
              </td>
              <td data-label="ชื่อสัญญา">
                {{ props.item.package_name || "-" }}
              </td>
              <td data-label="สถานะ">
                <v-chip small color="red" text-color="white">
                  {{ props.item.status || "deleted" }}
                </v-chip>
              </td>
              <td data-label="ราคาต่อรอบ" class="text-xs-right">
                {{
                  money(
                    props.item.normal_price_per_round || props.item.total_price
                  )
                }}
              </td>
              <td data-label="รอบที่ใช้แล้ว" class="text-xs-center">
                {{ props.item.used_rounds || 0 }}/{{
                  props.item.total_rounds || 0
                }}
              </td>
              <td data-label="ผู้ลบ">
                ID {{ props.item.deleted_by_id || "-" }}
              </td>
              <td data-label="ลบเมื่อ">
                {{ formatDateTime(props.item.delete_at) }}
              </td>
              <td data-label="จัดการ" class="text-xs-center">
                <v-tooltip top>
                  <v-btn
                    icon
                    slot="activator"
                    :loading="openingContractId === props.item.contract_id"
                    @click="selectContract(props.item)"
                  >
                    <v-icon small>visibility</v-icon>
                  </v-btn>
                  <span>เปิด</span>
                </v-tooltip>
              </td>
            </template>
            <template slot="no-data">
              <div class="pa-4 text-xs-center">
                <span v-if="!loadingDeleted">ไม่พบสัญญาที่ลบ</span>
                <span v-else>กำลังโหลดสัญญาที่ลบ...</span>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { globalAxios } from "@/axios-path.js";
import ContractDateTimePicker from "@/components/back-office/Messenger/ContractDateTimePicker.vue";

export default {
  components: {
    ContractDateTimePicker,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    initialProvince: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      loadingDeleted: false,
      loadingProvinces: false,
      loadingVehicles: false,
      loadingAddons: false,
      loadingSavedPoints: false,
      loadingCompanyCredit: false,
      savedPointLoadError: false,
      savingPointIndex: null,
      saving: false,
      calculatingSnapshot: false,
      dialog: false,
      deletedDialog: false,
      creatingRoundId: null,
      deletingContractId: null,
      duplicatingContractId: null,
      openingContractId: null,
      ownerMode: "me",
      ownerSearch: "",
      ownerSearchTimer: null,
      ownerSearchRequestId: 0,
      selectedOwnerCustomer: null,
      customerOptions: [],
      searchingCustomers: false,
      companySearch: "",
      companySearchTimer: null,
      companySearchRequestId: 0,
      companyCreditRequestId: 0,
      selectedOwnerCompany: null,
      companyOptions: [],
      searchingCompanies: false,
      selectedProvinceSlug: "",
      filters: {
        status: "",
      },
      pagination: {
        page: 1,
        rowsPerPage: 10,
      },
      deletedPagination: {
        page: 1,
        rowsPerPage: 10,
      },
      creditTransactionPagination: {
        sortBy: "create_at",
        descending: true,
        page: 1,
        rowsPerPage: 10,
      },
      roundPagination: {
        sortBy: "round_number",
        descending: false,
        page: 1,
        rowsPerPage: 20,
      },
      headers: [
        {
          text: "สัญญา",
          value: "contract_code",
          sortable: false,
          align: "center",
        },
        {
          text: "เจ้าของสัญญา",
          value: "owner_full_name",
          sortable: false,
          align: "center",
        },
        {
          text: "ชื่อสัญญา",
          value: "package_name",
          sortable: false,
          align: "center",
        },
        { text: "สถานะ", value: "status", align: "center", sortable: false },
        {
          text: "ราคาต่อรอบ",
          value: "total_price",
          align: "right",
          sortable: false,
        },
        {
          text: "รอบที่ใช้แล้ว",
          value: "used_rounds",
          align: "center",
          sortable: false,
        },
        {
          text: "วันที่เริ่ม",
          value: "schedule_start_at",
          align: "center",
          sortable: false,
        },
        { text: "จัดการ", value: "action", align: "center", sortable: false },
      ],
      deletedHeaders: [
        { text: "สัญญา", value: "contract_code", sortable: false },
        { text: "เจ้าของสัญญา", value: "owner_full_name", sortable: false },
        { text: "ชื่อสัญญา", value: "package_name", sortable: false },
        { text: "สถานะ", value: "status", sortable: false },
        {
          text: "ราคาต่อรอบ",
          value: "total_price",
          align: "right",
          sortable: false,
        },
        {
          text: "รอบที่ใช้แล้ว",
          value: "used_rounds",
          align: "center",
          sortable: false,
        },
        { text: "ผู้ลบ", value: "deleted_by_id", sortable: false },
        { text: "ลบเมื่อ", value: "delete_at", sortable: false },
        { text: "จัดการ", value: "action", align: "center", sortable: false },
      ],
      routeHeaders: [
        { text: "#", value: "number", align: "center", sortable: false },
        { text: "จุด", value: "saved_point_name", sortable: false },
        { text: "ที่อยู่", value: "address", sortable: false },
        { text: "ชื่อผู้ติดต่อ", value: "to", sortable: false },
        { text: "เบอร์โทรศัพท์", value: "tel", sortable: false },
        { text: "หมายเหตุ", value: "remark", sortable: false },
        { text: "ละติจูด", value: "lat", sortable: false },
        { text: "ลองจิจูด", value: "lng", sortable: false },
        { text: "", value: "action", align: "center", sortable: false },
      ],
      roundHeaders: [
        {
          text: "รอบ",
          value: "round_number",
          align: "center",
          sortable: false,
        },
        { text: "กำหนดส่ง", value: "scheduled_at", sortable: false },
        { text: "เวลาสร้างออเดอร์", value: "order_create_at", sortable: false },
        { text: "สถานะ", value: "status", sortable: false },
        {
          text: "เลขที่ออเดอร์",
          value: "messenger_order_code",
          sortable: false,
        },
        {
          text: "เครดิต",
          value: "required_credit_amount",
          align: "right",
          sortable: false,
        },
        { text: "จัดการ", value: "action", align: "center", sortable: false },
      ],
      creditTransactionHeaders: [
        { text: "วันที่", value: "create_at" },
        { text: "ประเภทรายการ", value: "transaction_type" },
        { text: "ยอดก่อนทำรายการ", value: "balance_before", align: "right" },
        { text: "จำนวนเงิน", value: "amount", align: "right" },
        { text: "ยอดหลังทำรายการ", value: "balance_after", align: "right" },
        { text: "เลขอ้างอิง", value: "messenger_order_code" },
      ],
      statusFilterItems: [
        { text: "ทั้งหมด", value: "" },
        { text: "ฉบับร่าง", value: "draft" },
        { text: "กำลังใช้งาน", value: "active" },
        { text: "สร้างออเดอร์ครบแล้ว", value: "orders_created" },
        { text: "พักการใช้งาน", value: "paused" },
        { text: "เสร็จสิ้น", value: "completed" },
        { text: "หมดอายุ", value: "expired" },
        { text: "ยกเลิก", value: "cancelled" },
      ],
      contracts: [],
      deletedContracts: [],
      vehicles: [],
      addons: [],
      savedPoints: [],
      savedPointRequestId: 0,
      selectedSavedPointId: null,
      form: this.emptyForm(),
      routePoints: [],
      priceSnapshotInputSignature: null,
      savedContractInputSignature: null,
      roundGenerator: {
        count: 1,
        interval_minutes: 1440,
        first_scheduled_at: moment()
          .add(1, "day")
          .hour(9)
          .minute(0)
          .second(0)
          .format("YYYY-MM-DDTHH:mm:ss"),
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.Info || {};
    },
    currentUserDisplay() {
      const user = this.currentUser;
      const fullName = [user.user_first_name, user.user_last_name]
        .filter(Boolean)
        .join(" ");
      const phone = this.formatCustomerPhone(user);
      const details = [fullName || "ผู้ใช้งาน Back Office ปัจจุบัน", phone]
        .filter(Boolean)
        .join(" - ");
      return user.user_id ? details + " (ID " + user.user_id + ")" : details;
    },
    ownerSearchHint() {
      if (!this.ownerSearch || this.ownerSearch.trim().length < 2) {
        return "พิมพ์อย่างน้อย 2 ตัวอักษร";
      }
      return this.searchingCustomers ? "กำลังค้นหาลูกค้า..." : "ไม่พบลูกค้า";
    },
    companySearchHint() {
      if (!this.companySearch || this.companySearch.trim().length < 2) {
        return "พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหาบริษัท";
      }
      return this.searchingCompanies ? "กำลังค้นหาบริษัท..." : "ไม่พบบริษัท";
    },
    province() {
      return this.selectedProvinceSlug;
    },
    provinceLocked() {
      return !!(this.embedded && this.provinceSlug(this.initialProvince));
    },
    provinceOptions() {
      return (this.$store.state.fetchProvinceBack.provinceData || [])
        .map((item) => ({
          text: item.province_name_th || item.province_name_en,
          value: this.provinceSlug(item.province_name_en),
          id: Number(item.province_id),
        }))
        .filter((item) => item.value);
    },
    selectedProvinceName() {
      const selected = this.provinceOptions.find(
        (item) => item.value === this.selectedProvinceSlug
      );
      return selected ? selected.text : "";
    },
    vehicleOptions() {
      return this.vehicles.map((vehicle) => {
        const title =
          vehicle.vehicle_title_th ||
          vehicle.vehicle_title_en ||
          "รถรหัส " + vehicle.vehicle_id;
        const englishTitle =
          vehicle.vehicle_title_en && vehicle.vehicle_title_en !== title
            ? " / " + vehicle.vehicle_title_en
            : "";
        return {
          text: title + englishTitle,
          value: Number(vehicle.vehicle_id),
          disabled: !vehicle.vehicle_active,
        };
      });
    },
    addonOptions() {
      const vehicleId = Number(this.form.vehicle_id);
      return this.addons
        .filter(
          (addon) =>
            Number(addon.addon_vehicle_id) === vehicleId &&
            !this.isExtraTimeAddon(addon)
        )
        .sort(
          (left, right) => Number(left.addon_price) - Number(right.addon_price)
        )
        .map((addon) => ({
          text:
            (addon.addon_title_th || addon.addon_title_en || "บริการเสริม") +
            " (+" +
            this.money(addon.addon_price) +
            ")",
          value: Number(addon.addon_type_id),
          price: Number(addon.addon_price || 0),
        }));
    },
    savedPointOptions() {
      return this.savedPoints.map((point) => {
        const name = point.saved_point_name || point.saved_point_to;
        const address = point.saved_point_address_text || "ไม่มีที่อยู่";
        return Object.assign({}, point, {
          display_text: name ? name + " - " + address : address,
        });
      });
    },
    savedPointHint() {
      if (this.ownerMode === "company" && !this.form.owner_company_id) {
        return "กรุณาเลือกบริษัทก่อน";
      }
      if (this.ownerMode !== "company" && !this.form.owner_user_id) {
        return "กรุณาเลือกเจ้าของสัญญาก่อน";
      }
      if (this.savedPointLoadError) return "ไม่สามารถโหลดจุดที่บันทึกไว้ได้";
      return this.loadingSavedPoints
        ? "กำลังโหลดจุดที่บันทึกไว้..."
        : this.ownerMode === "company"
        ? "บริษัทนี้ยังไม่มีจุดที่บันทึกไว้"
        : "ลูกค้ารายนี้ยังไม่มีจุดที่บันทึกไว้";
    },
    selectedAddonPrice() {
      const selectedIds = (this.form.addon_ids || []).map(Number);
      return this.addonOptions.reduce(
        (total, addon) =>
          selectedIds.indexOf(Number(addon.value)) >= 0
            ? total + Number(addon.price || 0)
            : total,
        0
      );
    },
    isEditing() {
      return !!this.form.contract_id;
    },
    isDeleted() {
      return !!this.form.delete_at;
    },
    lockedByOrders() {
      return Number(this.form.used_rounds || 0) > 0;
    },
    canEditContractDetails() {
      return (
        !this.isDeleted &&
        (!this.isEditing || ["draft", "paused"].indexOf(this.form.status) >= 0)
      );
    },
    canEditPriceInputs() {
      return this.canEditContractDetails && !this.lockedByOrders;
    },
    canEditSchedule() {
      return this.canEditContractDetails;
    },
    derivedRoundCount() {
      return Array.isArray(this.form.rounds) ? this.form.rounds.length : 0;
    },
    contractPaymentType() {
      return this.ownerMode === "company" ? 5 : 1;
    },
    derivedScheduleStartAt() {
      return this.roundScheduleBoundary("start");
    },
    derivedScheduleEndAt() {
      return this.roundScheduleBoundary("end");
    },
    hasPriceSnapshot() {
      return !!this.form.google_price_calculated_at;
    },
    priceSnapshotDirty() {
      return (
        this.hasPriceSnapshot &&
        this.priceSnapshotInputSignature !== null &&
        this.priceSnapshotInputSignature !== this.pricingInputSignature()
      );
    },
    hasUnsavedChanges() {
      return (
        this.isEditing &&
        this.savedContractInputSignature !== null &&
        this.savedContractInputSignature !== this.contractInputSignature()
      );
    },
    hasOverdueRounds() {
      if (["draft", "paused"].indexOf(this.form.status) < 0) return false;
      const now = moment();
      return (this.form.rounds || []).some((round) => {
        if (this.roundLocked(round)) return false;
        const orderCreateAt = round.order_create_at
          ? moment(round.order_create_at)
          : moment(round.scheduled_at).subtract(
              Number(this.form.order_create_before_minutes || 0),
              "minutes"
            );
        return (
          orderCreateAt.isValid() && orderCreateAt.valueOf() <= now.valueOf()
        );
      });
    },
    canCalculate() {
      return (
        !this.isDeleted &&
        this.canEditSchedule &&
        !!this.form.vehicle_id &&
        this.derivedRoundCount > 0 &&
        this.derivedRoundCount <= 500 &&
        this.routePoints.length >= 2 &&
        this.routePoints.length <= 20 &&
        !this.routePoints.some((stop) => stop.lat === null || stop.lng === null)
      );
    },
    canActivate() {
      return (
        this.isEditing &&
        !this.isDeleted &&
        !this.form.price_snapshot_token &&
        ["draft", "paused"].indexOf(this.form.status) >= 0 &&
        !this.hasUnsavedChanges &&
        !this.hasOverdueRounds &&
        this.routePoints.length >= 2 &&
        !this.priceSnapshotDirty &&
        Number(this.form.normal_price_per_round || this.form.total_price || 0) >
          0 &&
        this.form.rounds &&
        this.form.rounds.length > 0
      );
    },
    remainingRounds() {
      return Math.max(
        0,
        Number(this.form.total_rounds || 0) - Number(this.form.used_rounds || 0)
      );
    },
    distanceText() {
      if (this.form.total_distance) return this.form.total_distance;
      if (this.form.distance_km) return this.form.distance_km + " km";
      return "-";
    },
    waypointText() {
      const order =
        this.form.waypoint_order || this.form.waypoint_order_snapshot;
      if (!order || !order.length) return "-";
      return order.join(", ");
    },
    summaryCards() {
      return [
        {
          label: "สัญญาทั้งหมด",
          value: this.contracts.length,
          icon: "assignment",
          color: "primary",
        },
        {
          label: "กำลังใช้งาน",
          value: this.contracts.filter((item) => item.status === "active")
            .length,
          icon: "play_circle_filled",
          color: "green",
        },
        {
          label: "ฉบับร่าง",
          value: this.contracts.filter((item) => item.status === "draft")
            .length,
          icon: "edit",
          color: "orange",
        },
        {
          label: "ออเดอร์ที่สร้างแล้ว",
          value: this.contracts.reduce(
            (sum, item) => sum + Number(item.used_rounds || 0),
            0
          ),
          icon: "send",
          color: "blue",
        },
      ];
    },
  },
  watch: {
    ownerSearch(value) {
      if (this.ownerMode !== "customer") return;
      if (
        this.selectedOwnerCustomer &&
        value === this.selectedOwnerCustomer.display_text
      ) {
        return;
      }
      clearTimeout(this.ownerSearchTimer);
      const requestId = ++this.ownerSearchRequestId;
      const query = String(value || "").trim();
      if (query.length < 2) {
        this.searchingCustomers = false;
        this.customerOptions = this.selectedOwnerCustomer
          ? [this.selectedOwnerCustomer]
          : [];
        return;
      }
      this.searchingCustomers = true;
      this.ownerSearchTimer = setTimeout(() => {
        this.searchCustomers(query, requestId);
      }, 400);
    },
    companySearch(value) {
      if (this.ownerMode !== "company") return;
      if (
        this.selectedOwnerCompany &&
        value === this.selectedOwnerCompany.display_text
      )
        return;
      clearTimeout(this.companySearchTimer);
      const requestId = ++this.companySearchRequestId;
      const query = String(value || "").trim();
      if (query.length < 2) {
        this.companyOptions = [];
        this.searchingCompanies = false;
        return;
      }
      this.searchingCompanies = true;
      this.companySearchTimer = setTimeout(
        () => this.searchCompanies(query, requestId),
        400
      );
    },
  },
  async mounted() {
    await this.fetchProvinces();
    this.setBreadcrumb();
    await this.fetchVehicles();
    await this.fetchAddons();
    await this.fetchContracts();
  },
  beforeDestroy() {
    clearTimeout(this.ownerSearchTimer);
    clearTimeout(this.companySearchTimer);
    this.ownerSearchRequestId += 1;
    this.companySearchRequestId += 1;
    this.savedPointRequestId += 1;
    this.companyCreditRequestId += 1;
  },
  methods: {
    emptyForm() {
      return {
        owner_mode: "me",
        owner_user_id: null,
        owner_company_id: null,
        owner_full_name: "",
        province_id: null,
        vehicle_id: null,
        package_name: "",
        package_note: "",
        total_rounds: 1,
        used_rounds: 0,
        completed_rounds: 0,
        remaining_rounds: 1,
        order_create_before_minutes: 60,
        add_on_price: 0,
        addon_ids: [],
        schedule_start_at: moment()
          .add(1, "day")
          .hour(9)
          .minute(0)
          .second(0)
          .format("YYYY-MM-DDTHH:mm:ss"),
        schedule_end_at: moment()
          .add(1, "month")
          .hour(18)
          .minute(0)
          .second(0)
          .format("YYYY-MM-DDTHH:mm:ss"),
        status: "draft",
        price_snapshot_token: null,
        rounds: [],
      };
    },
    setBreadcrumb() {
      if (this.embedded) return;
      const path = [
        {
          text: "เลือกจังหวัด",
          disabled: false,
          href: "/weserve_admin_login/select/",
        },
        { text: "สัญญา B2B", disabled: true },
      ];
      this.$store.commit("backOffice/saveRecentRoute", path);
    },
    async fetchProvinces() {
      this.loadingProvinces = true;
      try {
        if (!(this.$store.state.fetchProvinceBack.provinceData || []).length) {
          await this.$store.dispatch("fetchProvinceBack/getData");
        }
        const requestedProvince =
          this.initialProvince ||
          this.$route.query.province ||
          this.$route.params.province ||
          this.$store.state.backOffice.province;
        const requestedSlug = this.provinceSlug(requestedProvince);
        const lockedSlug = this.provinceLocked
          ? this.provinceSlug(this.initialProvince)
          : "";
        const hasRequested = this.provinceOptions.some(
          (item) => item.value === requestedSlug
        );
        this.selectedProvinceSlug = lockedSlug
          ? lockedSlug
          : hasRequested
          ? requestedSlug
          : this.provinceOptions[0]
          ? this.provinceOptions[0].value
          : requestedSlug;
      } finally {
        this.loadingProvinces = false;
      }
    },
    provinceSlug(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/province/g, "")
        .replace(/[^a-z0-9]/g, "");
    },
    changeProvince() {
      if (this.provinceLocked) return;
      this.savedPointRequestId += 1;
      this.loadingSavedPoints = false;
      this.dialog = false;
      this.deletedDialog = false;
      this.form = this.emptyForm();
      this.savedPoints = [];
      if (
        !this.embedded &&
        this.$route.query.province !== this.selectedProvinceSlug
      ) {
        this.$router.replace({
          path: "/weserve_admin_login/-/messenger/contracts",
          query: Object.assign({}, this.$route.query, {
            province: this.selectedProvinceSlug,
          }),
        });
      }
      this.fetchVehicles();
      this.fetchAddons();
      this.fetchContracts();
    },
    endpoint(path) {
      return "/api/v1/mebf/" + this.province + "/" + path;
    },
    normalizeResponse(res, fallback) {
      if (!res) return fallback;
      if (res.data && Array.isArray(res.data)) return res.data;
      if (res.data && res.data.results) return res.data.results;
      if (res.results) return res.results;
      if (Array.isArray(res)) return res;
      if (res.data && typeof res.data === "object") return res.data;
      return res;
    },
    async fetchContracts() {
      if (!this.province) {
        this.contracts = [];
        return;
      }
      this.loading = true;
      try {
        const params = {};
        if (this.filters.status) params.status = this.filters.status;
        const res = await globalAxios.get(this.endpoint("contracts"), {
          params,
        });
        const data = this.normalizeResponse(res, []);
        this.contracts = Array.isArray(data) ? data : [];
      } catch (err) {
        this.showError("ไม่สามารถโหลดรายการสัญญาได้");
      } finally {
        this.loading = false;
      }
    },
    async fetchVehicles() {
      if (!this.province) {
        this.vehicles = [];
        return;
      }
      this.loadingVehicles = true;
      try {
        const res = await globalAxios.get(this.endpoint("vehicle"));
        const data = this.normalizeResponse(res, []);
        this.vehicles = Array.isArray(data) ? data : [];
      } catch (err) {
        this.vehicles = [];
        this.showError("ไม่สามารถโหลดประเภทรถได้");
      } finally {
        this.loadingVehicles = false;
      }
    },
    async fetchAddons() {
      if (!this.province) {
        this.addons = [];
        return;
      }
      this.loadingAddons = true;
      try {
        const res = await globalAxios.get(
          "/api/v1/" + this.province + "/startapp"
        );
        this.addons = res && Array.isArray(res.addons) ? res.addons : [];
      } catch (err) {
        this.addons = [];
        this.showError("ไม่สามารถโหลดบริการเสริมได้");
      } finally {
        this.loadingAddons = false;
      }
    },
    async fetchSavedPoints() {
      const requestId = ++this.savedPointRequestId;
      const ownerParams = this.savedPointOwnerParams();
      if (!this.province || !ownerParams) {
        this.savedPoints = [];
        this.savedPointLoadError = false;
        return;
      }
      this.loadingSavedPoints = true;
      this.savedPointLoadError = false;
      try {
        const response = await globalAxios.get(this.endpoint("savedpoints"), {
          params: ownerParams,
        });
        const body =
          response &&
          response.data &&
          !Array.isArray(response.data) &&
          Object.prototype.hasOwnProperty.call(response.data, "status")
            ? response.data
            : response;
        if (
          requestId !== this.savedPointRequestId ||
          !this.isSameSavedPointOwner(ownerParams, this.savedPointOwnerParams())
        )
          return;
        this.savedPoints =
          body && Array.isArray(body.data)
            ? body.data
            : Array.isArray(body)
            ? body
            : [];
      } catch (err) {
        if (requestId !== this.savedPointRequestId) return;
        this.savedPoints = [];
        this.savedPointLoadError = true;
      } finally {
        if (requestId === this.savedPointRequestId) {
          this.loadingSavedPoints = false;
        }
      }
    },
    async openDeletedHistory() {
      this.deletedDialog = true;
      await this.fetchDeletedContracts();
    },
    async fetchDeletedContracts() {
      if (!this.province) {
        this.deletedContracts = [];
        return;
      }
      this.loadingDeleted = true;
      try {
        const res = await globalAxios.get(this.endpoint("contracts"), {
          params: { deleted: true },
        });
        const data = this.normalizeResponse(res, []);
        this.deletedContracts = Array.isArray(data) ? data : [];
      } catch (err) {
        this.showError("ไม่สามารถโหลดประวัติสัญญาที่ลบได้");
      } finally {
        this.loadingDeleted = false;
      }
    },
    openCreate() {
      this.form = this.emptyForm();
      this.priceSnapshotInputSignature = null;
      this.savedContractInputSignature = null;
      this.ownerMode = "me";
      this.selectedOwnerCustomer = null;
      this.customerOptions = [];
      this.ownerSearch = "";
      this.selectedOwnerCompany = null;
      this.companyOptions = [];
      this.companySearch = "";
      this.applyCurrentUserAsOwner();
      this.form.province_id = this.selectedProvinceId();
      this.routePoints = [];
      this.selectedSavedPointId = null;
      this.form.rounds = [];
      this.roundGenerator.count = Number(this.form.total_rounds || 1);
      this.roundGenerator.first_scheduled_at = this.form.schedule_start_at;
      this.dialog = true;
      this.fetchSavedPoints();
    },
    async selectContract(item) {
      const contractId = item.contract_id;
      this.openingContractId = contractId;
      try {
        const res = await globalAxios.get(
          this.endpoint("contracts/" + contractId),
          {
            params: item.delete_at ? { include_deleted: true } : {},
          }
        );
        if (this.openingContractId !== contractId) return;
        const data = this.normalizeResponse(res, {});
        this.form = Object.assign(this.emptyForm(), data);
        // The page-level province selector is the source of truth. This also
        // repairs stale IDs when an older contract is edited.
        this.form.province_id = this.selectedProvinceId();
        this.form.vehicle_id =
          this.form.vehicle_id ||
          (typeof this.form.vehicle === "object" && this.form.vehicle
            ? this.form.vehicle.vehicle_id
            : this.form.vehicle);
        this.form.rounds = this.form.rounds || [];
        this.syncOwnerSelection();
        await this.fetchSavedPoints();
        this.routePoints = this.extractRoutePoints(this.form);
        this.capturePriceSnapshotInputs();
        this.captureContractInputs();
        this.selectedSavedPointId = null;
        this.roundGenerator.count = Number(
          this.form.total_rounds || this.form.rounds.length || 1
        );
        this.roundGenerator.first_scheduled_at =
          this.form.schedule_start_at || this.roundGenerator.first_scheduled_at;
        if (item.delete_at) this.deletedDialog = false;
        this.dialog = true;
      } catch (err) {
        if (this.openingContractId === contractId) {
          this.showError("ไม่สามารถโหลดรายละเอียดสัญญาได้");
        }
      } finally {
        if (this.openingContractId === contractId) {
          this.openingContractId = null;
        }
      }
    },
    closeDialog() {
      this.dialog = false;
    },
    changeOwnerMode(mode) {
      this.savedPointRequestId += 1;
      this.companyCreditRequestId += 1;
      this.loadingSavedPoints = false;
      this.loadingCompanyCredit = false;
      this.ownerSearch = "";
      this.customerOptions = [];
      this.selectedOwnerCustomer = null;
      this.selectedOwnerCompany = null;
      this.companyOptions = [];
      this.companySearch = "";
      this.form.owner_mode = mode;
      this.form.owner_company_id = null;
      this.$set(this.form, "credit_summary", null);
      this.$set(this.form, "credit_transactions", []);
      this.selectedSavedPointId = null;
      if (mode === "me") {
        this.applyCurrentUserAsOwner();
        this.fetchSavedPoints();
      } else if (mode === "customer") {
        this.form.owner_user_id = null;
        this.form.owner_full_name = "";
        this.savedPoints = [];
        this.savedPointLoadError = false;
      } else {
        this.form.owner_user_id = null;
        this.form.owner_full_name = "";
        this.savedPoints = [];
      }
    },
    applyCurrentUserAsOwner() {
      const user = this.currentUser;
      this.form.owner_user_id = user.user_id || null;
      this.form.owner_full_name = [user.user_first_name, user.user_last_name]
        .filter(Boolean)
        .join(" ");
    },
    syncOwnerSelection() {
      this.ownerMode =
        this.form.owner_mode || (this.form.owner_company_id ? "company" : "me");
      if (this.ownerMode === "company") {
        this.selectedOwnerCompany = {
          company_provider_wesmart_id: this.form.owner_company_id,
          display_text:
            (this.form.owner_full_name || "บริษัท") +
            " (ID " +
            this.form.owner_company_id +
            ")",
        };
        this.companyOptions = [this.selectedOwnerCompany];
        return;
      }
      if (
        this.ownerMode === "me" ||
        (!this.form.owner_mode &&
          this.currentUser.user_id &&
          Number(this.form.owner_user_id) === Number(this.currentUser.user_id))
      ) {
        this.ownerMode = "me";
        this.selectedOwnerCustomer = null;
        this.customerOptions = [];
        return;
      }
      this.ownerMode = "customer";
      this.selectedOwnerCustomer = {
        user_id: this.form.owner_user_id,
        user_first_name: this.form.owner_full_name || "ลูกค้า",
        user_last_name: "",
        display_text:
          (this.form.owner_full_name || "ลูกค้า") +
          " (ID " +
          this.form.owner_user_id +
          ")",
      };
      this.customerOptions = [this.selectedOwnerCustomer];
    },
    async searchCompanies(query, requestId) {
      this.searchingCompanies = true;
      try {
        const res = await globalAxios.get(
          this.endpoint("contracts/companies"),
          { params: { search: query } }
        );
        const rows = this.normalizeResponse(res, []);
        if (
          requestId !== this.companySearchRequestId ||
          String(this.companySearch || "").trim() !== query
        )
          return;
        this.companyOptions = (Array.isArray(rows) ? rows : []).map((item) =>
          Object.assign({}, item, {
            display_text:
              (item.company_provider_wesmart_name || "บริษัท") +
              " - " +
              (item.company_provider_wesmart_tel ||
                item.company_provider_wesmart_email ||
                ""),
          })
        );
      } catch (err) {
        if (requestId === this.companySearchRequestId) {
          this.companyOptions = [];
        }
      } finally {
        if (requestId === this.companySearchRequestId) {
          this.searchingCompanies = false;
        }
      }
    },
    selectOwnerCompany(company) {
      this.savedPointRequestId += 1;
      this.companyCreditRequestId += 1;
      this.loadingSavedPoints = false;
      this.loadingCompanyCredit = false;
      this.selectedSavedPointId = null;
      this.form.owner_company_id = company
        ? company.company_provider_wesmart_id
        : null;
      this.form.owner_full_name = company
        ? company.company_provider_wesmart_name
        : "";
      this.savedPoints = [];
      this.$set(this.form, "credit_summary", null);
      this.$set(this.form, "credit_transactions", []);
      if (company) {
        this.fetchSavedPoints();
        this.fetchCompanyCredit();
      }
    },
    async fetchCompanyCredit(showError) {
      const companyId = Number(this.form.owner_company_id || 0);
      const requestId = ++this.companyCreditRequestId;
      if (this.ownerMode !== "company" || !companyId) {
        this.$set(this.form, "credit_summary", null);
        this.$set(this.form, "credit_transactions", []);
        this.loadingCompanyCredit = false;
        return;
      }
      this.loadingCompanyCredit = true;
      try {
        const response = await globalAxios.get("/api/v1/mebf/company_credit", {
          params: { company_id: companyId },
        });
        if (
          requestId !== this.companyCreditRequestId ||
          Number(this.form.owner_company_id || 0) !== companyId
        )
          return;
        const body = this.normalizeResponse(response, {});
        const account = body.account || {};
        const contractId = String(this.form.contract_id || "");
        const contractTransactions = (body.transactions || []).filter(
          (item) =>
            contractId &&
            item.source_type === "messenger_contract" &&
            String(item.source_id) === contractId
        );
        const consumed = contractTransactions
          .filter((item) => item.transaction_type === "order_deduction")
          .reduce((total, item) => total + Number(item.amount || 0), 0);
        this.$set(this.form, "credit_summary", {
          company_id: companyId,
          current_balance: account.current_balance || 0,
          reserved_balance: account.reserved_balance || 0,
          available_balance: account.available_balance || 0,
          consumed_by_contract: consumed,
        });
        this.$set(this.form, "credit_transactions", contractTransactions);
      } catch (err) {
        if (requestId !== this.companyCreditRequestId) return;
        this.$set(this.form, "credit_summary", null);
        this.$set(this.form, "credit_transactions", []);
        if (showError) {
          this.showError(
            this.apiErrorMessage(err, "ไม่สามารถโหลดเครดิตบริษัทได้")
          );
        }
      } finally {
        if (requestId === this.companyCreditRequestId) {
          this.loadingCompanyCredit = false;
        }
      }
    },
    async searchCustomers(query, requestId) {
      this.searchingCustomers = true;
      try {
        const numericQuery = query.replace(/[^0-9]/g, "");
        const isPhone =
          numericQuery.length >= 2 && numericQuery.length >= query.length - 2;
        const params = {
          page_size: 20,
          current_page: 1,
        };
        if (isPhone) {
          params.search_tel = numericQuery.replace(/^0/, "");
        } else {
          const nameParts = query.split(/\s+/).filter(Boolean);
          params.search_first_name = nameParts.shift() || query;
          if (nameParts.length) params.search_last_name = nameParts.join(" ");
        }
        const response = await this.$store.dispatch(
          "fetchCustomerBack/getData",
          params
        );
        const customers =
          response && Array.isArray(response.data) ? response.data : [];
        if (
          requestId !== this.ownerSearchRequestId ||
          String(this.ownerSearch || "").trim() !== query
        )
          return;
        this.customerOptions = customers.map((customer) =>
          Object.assign({}, customer, {
            display_text: this.customerDisplay(customer),
          })
        );
        if (
          this.selectedOwnerCustomer &&
          !this.customerOptions.some(
            (customer) =>
              Number(customer.user_id) ===
              Number(this.selectedOwnerCustomer.user_id)
          )
        ) {
          this.customerOptions.unshift(this.selectedOwnerCustomer);
        }
      } catch (err) {
        if (requestId === this.ownerSearchRequestId) {
          this.customerOptions = this.selectedOwnerCustomer
            ? [this.selectedOwnerCustomer]
            : [];
          this.showError("ไม่สามารถค้นหาลูกค้าได้");
        }
      } finally {
        if (requestId === this.ownerSearchRequestId) {
          this.searchingCustomers = false;
        }
      }
    },
    selectOwnerCustomer(customer) {
      this.savedPointRequestId += 1;
      this.loadingSavedPoints = false;
      this.selectedSavedPointId = null;
      if (!customer) {
        this.form.owner_user_id = null;
        this.form.owner_full_name = "";
        this.savedPoints = [];
        this.savedPointLoadError = false;
        return;
      }
      this.form.owner_user_id = customer.user_id;
      this.form.owner_full_name = [
        customer.user_first_name,
        customer.user_last_name,
      ]
        .filter(Boolean)
        .join(" ");
      this.fetchSavedPoints();
    },
    customerDisplay(customer) {
      const fullName = [customer.user_first_name, customer.user_last_name]
        .filter(Boolean)
        .join(" ");
      const phone = this.formatCustomerPhone(customer);
      return [fullName || "ลูกค้า", phone, "รหัส " + customer.user_id]
        .filter(Boolean)
        .join(" - ");
    },
    formatCustomerPhone(customer) {
      if (!customer || !customer.user_tel) return "";
      const country = String(customer.user_country_tel || "");
      const tel = String(customer.user_tel || "");
      if (country === "66" && tel.charAt(0) !== "0") return "0" + tel;
      return country && country !== "66" ? "+" + country + tel : tel;
    },
    changeVehicle() {
      this.form.addon_ids = [];
    },
    isExtraTimeAddon(addon) {
      const title = [addon.addon_title_th, addon.addon_title_en]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        title.indexOf("ค่าบริการนอกเวลาทำการ") >= 0 ||
        title.indexOf("outside business hours") >= 0 ||
        title.indexOf("extra time") >= 0
      );
    },
    blankStop() {
      return {
        saved_point_id: null,
        saved_point_name: "",
        address: "",
        lat: null,
        lng: null,
        to: "",
        tel: "",
        remark: "",
      };
    },
    stopName(stop) {
      if (!stop) return "จุดที่กำหนดเอง";
      return (
        stop.saved_point_name ||
        (stop.saved_point_id ? "จุดที่บันทึกไว้" : "จุดที่กำหนดเอง")
      );
    },
    savedPointStop(savedPointId) {
      const point = this.savedPoints.find(
        (item) => Number(item.saved_point_id) === Number(savedPointId)
      );
      if (!point) return null;
      const ownerParams = this.savedPointOwnerParams() || {};
      return {
        saved_point_id: Number(point.saved_point_id),
        saved_point_owner_mode:
          this.ownerMode === "company" ? "company" : "customer",
        saved_point_owner_id: Number(
          ownerParams.company_id || ownerParams.owner_user_id || 0
        ),
        saved_point_name: point.saved_point_name || "",
        address: point.saved_point_address_text || "",
        lat: Number(point.saved_point_latitude) || null,
        lng: Number(point.saved_point_longitude) || null,
        to: point.saved_point_to || "",
        tel: point.saved_point_tel || "",
        remark: point.saved_point_remark || "",
      };
    },
    appendSavedPoint() {
      if (!this.selectedSavedPointId || this.routePoints.length >= 20) return;
      const stop = this.savedPointStop(this.selectedSavedPointId);
      if (!stop) return;
      this.routePoints.push(stop);
      this.selectedSavedPointId = null;
    },
    savedPointOwnerParams() {
      if (this.ownerMode === "company") {
        return this.form.owner_company_id
          ? { company_id: this.form.owner_company_id }
          : null;
      }
      return this.form.owner_user_id
        ? { owner_user_id: this.form.owner_user_id }
        : null;
    },
    isSameSavedPointOwner(left, right) {
      if (!left || !right) return left === right;
      return (
        Number(left.company_id || 0) === Number(right.company_id || 0) &&
        Number(left.owner_user_id || 0) === Number(right.owner_user_id || 0)
      );
    },
    canSaveCompanyPoint(stop) {
      return !!(
        this.ownerMode === "company" &&
        this.form.owner_company_id &&
        stop &&
        String(stop.saved_point_name || "").trim() &&
        String(stop.address || "").trim() &&
        stop.lat !== null &&
        stop.lat !== "" &&
        stop.lng !== null &&
        stop.lng !== ""
      );
    },
    isCompanySavedPoint(stop) {
      if (!stop || !stop.saved_point_id) return false;
      if (stop.saved_point_owner_mode) {
        return (
          stop.saved_point_owner_mode === "company" &&
          Number(stop.saved_point_owner_id || 0) ===
            Number(this.form.owner_company_id || 0)
        );
      }
      return this.savedPoints.some(
        (point) => Number(point.saved_point_id) === Number(stop.saved_point_id)
      );
    },
    applyCompanySavedPointOwner(stop, savedPointId) {
      this.$set(stop, "saved_point_id", Number(savedPointId));
      this.$set(stop, "saved_point_owner_mode", "company");
      this.$set(
        stop,
        "saved_point_owner_id",
        Number(this.form.owner_company_id)
      );
    },
    routePointsPayload() {
      return this.routePoints.map((stop) => {
        const point = {
          saved_point_id: stop.saved_point_id || null,
          saved_point_name: stop.saved_point_name || "",
          address: stop.address,
          lat: stop.lat === null || stop.lat === "" ? null : Number(stop.lat),
          lng: stop.lng === null || stop.lng === "" ? null : Number(stop.lng),
          to: stop.to || "",
          tel: stop.tel || "",
          remark: stop.remark || "",
        };
        if (stop.saved_point_owner_mode) {
          point.saved_point_owner_mode = stop.saved_point_owner_mode;
          point.saved_point_owner_id = stop.saved_point_owner_id || null;
        }
        return point;
      });
    },
    async persistCompanyPointOwner() {
      if (!this.form.contract_id) return;
      await globalAxios.put(
        this.endpoint("contracts/" + this.form.contract_id),
        { route_points: this.routePointsPayload() }
      );
    },
    async saveCompanyPoint(stop, stopIndex) {
      if (!this.canSaveCompanyPoint(stop)) return;
      this.savingPointIndex = stopIndex;
      try {
        const response = await globalAxios.post(
          this.endpoint("savedpoints"),
          Object.assign(this.savedPointOwnerParams(), {
            saved_point_name: String(stop.saved_point_name || "").trim(),
            saved_point_address_text: String(stop.address || "").trim(),
            saved_point_latitude: Number(stop.lat),
            saved_point_longitude: Number(stop.lng),
            saved_point_to: stop.to || "",
            saved_point_tel: stop.tel || "",
            saved_point_remark: stop.remark || "",
          })
        );
        const body = this.normalizeResponse(response, {});
        if (body && body.saved_point_id) {
          this.applyCompanySavedPointOwner(stop, body.saved_point_id);
        }
        await this.fetchSavedPoints();
        if (!this.isCompanySavedPoint(stop)) {
          const saved = this.savedPoints.find(
            (point) =>
              point.saved_point_name === stop.saved_point_name &&
              Number(point.saved_point_latitude) === Number(stop.lat) &&
              Number(point.saved_point_longitude) === Number(stop.lng)
          );
          if (saved) {
            this.applyCompanySavedPointOwner(stop, saved.saved_point_id);
          }
        }
        if (this.isCompanySavedPoint(stop)) {
          await this.persistCompanyPointOwner();
        }
      } catch (err) {
        this.showError(
          this.apiErrorMessage(err, "ไม่สามารถบันทึกจุดของบริษัทได้")
        );
      } finally {
        this.savingPointIndex = null;
      }
    },
    addStop() {
      if (this.routePoints.length < 20) this.routePoints.push(this.blankStop());
    },
    removeStop(index) {
      if (!this.lockedByOrders && !this.isDeleted) {
        this.routePoints.splice(index, 1);
      }
    },
    addRound() {
      if (this.form.rounds.length >= 500) {
        this.showError("สัญญามีได้สูงสุด 500 รอบ");
        return;
      }
      const nextNumber =
        (this.form.rounds || []).reduce(
          (highest, round) =>
            Math.max(highest, Number(round.round_number || 0)),
          0
        ) + 1;
      const lastRound = this.form.rounds[this.form.rounds.length - 1];
      const scheduledAt = moment(
        lastRound && lastRound.scheduled_at
          ? lastRound.scheduled_at
          : this.roundGenerator.first_scheduled_at || undefined
      )
        .add(
          lastRound ? Number(this.roundGenerator.interval_minutes || 1440) : 0,
          "minutes"
        )
        .format("YYYY-MM-DDTHH:mm:ss");
      this.form.rounds.push({
        round_number: nextNumber,
        scheduled_at: scheduledAt,
        status: "planned",
      });
    },
    removeRound(index) {
      this.form.rounds.splice(index, 1);
      if (!this.lockedByOrders) {
        this.form.rounds = this.form.rounds.map((round, roundIndex) => {
          round.round_number = roundIndex + 1;
          return round;
        });
      }
    },
    generateRounds() {
      const rounds = [];
      const count = Number(this.roundGenerator.count || 0);
      if (count < 1 || count > 500) {
        this.showError("จำนวนรอบต้องอยู่ระหว่าง 1 ถึง 500");
        return;
      }
      const interval = Math.max(
        1,
        Number(this.roundGenerator.interval_minutes || 1440)
      );
      const first = moment(
        this.roundGenerator.first_scheduled_at ||
          this.form.schedule_start_at ||
          undefined
      );
      for (let index = 0; index < count; index += 1) {
        rounds.push({
          round_number: index + 1,
          scheduled_at: first
            .clone()
            .add(index * interval, "minutes")
            .format("YYYY-MM-DDTHH:mm:ss"),
          status: "planned",
        });
      }
      this.form.rounds = rounds;
    },
    roundScheduleBoundary(position) {
      const schedules = (this.form.rounds || [])
        .map((round) => moment(round.scheduled_at, moment.ISO_8601, true))
        .filter((date) => date.isValid())
        .sort((left, right) => left.valueOf() - right.valueOf());
      if (!schedules.length) return "";
      return (
        position === "end" ? schedules[schedules.length - 1] : schedules[0]
      ).format("YYYY-MM-DDTHH:mm:ss");
    },
    extractRoutePoints(contract) {
      let source =
        contract.route_snapshot_data ||
        contract.route_snapshot ||
        contract.location ||
        contract.route_points ||
        [];
      if (typeof source === "string") {
        try {
          source = JSON.parse(source);
        } catch (err) {
          source = [];
        }
      }
      if (Array.isArray(source)) {
        return source.map(this.normalizeStop);
      }
      if (source && Array.isArray(source.location)) {
        return source.location.map(this.normalizeStop);
      }
      if (source && Array.isArray(source.points)) {
        return source.points.map(this.normalizeStop);
      }
      return [];
    },
    normalizeStop(stop) {
      return {
        saved_point_id:
          Number(stop.saved_point_id || stop.savedPointId || 0) || null,
        saved_point_owner_mode: stop.saved_point_owner_mode || null,
        saved_point_owner_id: Number(stop.saved_point_owner_id || 0) || null,
        saved_point_name: stop.saved_point_name || stop.name || "",
        address:
          stop.address ||
          stop.saved_point_address_text ||
          stop.location_name ||
          stop.name ||
          stop.user_address_text ||
          "",
        lat:
          Number(
            stop.lat ||
              stop.latitude ||
              stop.saved_point_latitude ||
              stop.location_lat ||
              stop.user_address_lat ||
              0
          ) || null,
        lng:
          Number(
            stop.lng ||
              stop.long ||
              stop.longitude ||
              stop.saved_point_longitude ||
              stop.location_long ||
              stop.user_address_long ||
              0
          ) || null,
        to: stop.to || stop.saved_point_to || stop.receiver || "",
        tel: stop.tel || stop.saved_point_tel || "",
        remark: stop.remark || stop.saved_point_remark || stop.note || "",
      };
    },
    payload(recalculate) {
      const rounds = (this.form.rounds || []).map((round, index) => ({
        round_id: round.round_id,
        round_number: Number(round.round_number || index + 1),
        scheduled_at: round.scheduled_at,
        status: round.status || "planned",
      }));
      const payload = {
        owner_mode: this.ownerMode,
        order_payment_type: this.contractPaymentType,
        owner_full_name: this.form.owner_full_name,
        province_id: this.selectedProvinceId(),
        vehicle_id: this.form.vehicle_id,
        package_name: this.form.package_name,
        package_note: this.form.package_note,
        total_rounds: rounds.length,
        order_create_before_minutes: Number(
          this.form.order_create_before_minutes || 0
        ),
        addon_ids: (this.form.addon_ids || []).map(Number),
        add_on_price: Number(this.selectedAddonPrice || 0),
        schedule_start_at: this.derivedScheduleStartAt,
        schedule_end_at: this.derivedScheduleEndAt,
        route_points: this.routePointsPayload(),
        rounds: rounds,
        recalculate_price: !!recalculate,
      };
      if (this.form.price_snapshot_token) {
        payload.price_snapshot_token = this.form.price_snapshot_token;
      }
      if (this.ownerMode === "customer") {
        payload.customer_user_id = this.form.owner_user_id;
      } else if (this.ownerMode === "company") {
        payload.company_id = this.form.owner_company_id;
      }
      return payload;
    },
    async calculateSnapshot() {
      if (!this.validatePricingInputs()) return;
      this.calculatingSnapshot = true;
      try {
        const response = await globalAxios.post(
          this.endpoint("contracts/calculate"),
          this.payload(false)
        );
        const preview = this.normalizeResponse(response, {});
        this.form = Object.assign({}, this.form, preview);
        this.capturePriceSnapshotInputs();
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถคำนวณราคาได้"));
      } finally {
        this.calculatingSnapshot = false;
      }
    },
    pricingInputSignature() {
      return JSON.stringify({
        vehicle_id: Number(this.form.vehicle_id || 0),
        addon_ids: (this.form.addon_ids || [])
          .map(Number)
          .sort((left, right) => left - right),
        route_points: (this.routePoints || []).map((stop) => ({
          lat: stop.lat === null || stop.lat === "" ? null : Number(stop.lat),
          lng: stop.lng === null || stop.lng === "" ? null : Number(stop.lng),
        })),
      });
    },
    capturePriceSnapshotInputs() {
      this.priceSnapshotInputSignature = this.hasPriceSnapshot
        ? this.pricingInputSignature()
        : null;
    },
    contractInputSignature() {
      return JSON.stringify({
        owner_mode: this.ownerMode,
        owner_user_id: Number(this.form.owner_user_id || 0),
        owner_company_id: Number(this.form.owner_company_id || 0),
        owner_full_name: this.form.owner_full_name || "",
        province_id: Number(this.form.province_id || 0),
        vehicle_id: Number(this.form.vehicle_id || 0),
        package_name: this.form.package_name || "",
        package_note: this.form.package_note || "",
        order_create_before_minutes: Number(
          this.form.order_create_before_minutes || 0
        ),
        addon_ids: (this.form.addon_ids || [])
          .map(Number)
          .sort((left, right) => left - right),
        route_points: (this.routePoints || []).map((stop) => ({
          saved_point_id: Number(stop.saved_point_id || 0),
          saved_point_name: stop.saved_point_name || "",
          address: stop.address || "",
          lat: stop.lat === null || stop.lat === "" ? null : Number(stop.lat),
          lng: stop.lng === null || stop.lng === "" ? null : Number(stop.lng),
          to: stop.to || "",
          tel: stop.tel || "",
          remark: stop.remark || "",
        })),
        rounds: (this.form.rounds || []).map((round) => ({
          round_id: Number(round.round_id || 0),
          round_number: Number(round.round_number || 0),
          scheduled_at: round.scheduled_at || "",
          status: round.status || "planned",
        })),
        price_snapshot_token: this.form.price_snapshot_token || null,
      });
    },
    captureContractInputs() {
      this.savedContractInputSignature = this.isEditing
        ? this.contractInputSignature()
        : null;
    },
    async createContract(recalculate) {
      if (!(recalculate ? this.validatePricingInputs() : this.validateDraft()))
        return;
      this.saving = true;
      try {
        const res = await globalAxios.post(
          this.endpoint("contracts"),
          this.payload(!!recalculate)
        );
        this.form = Object.assign(
          this.emptyForm(),
          this.normalizeResponse(res, {})
        );
        this.form.rounds = this.form.rounds || [];
        this.routePoints = this.extractRoutePoints(this.form);
        this.capturePriceSnapshotInputs();
        this.captureContractInputs();
        await this.fetchContracts();
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถสร้างสัญญาได้"));
      } finally {
        this.saving = false;
      }
    },
    async saveContract(recalculate) {
      if (this.isDeleted) {
        this.showError("สัญญาที่ลบแล้วเปิดดูได้อย่างเดียว");
        return;
      }
      if (!(recalculate ? this.validatePricingInputs() : this.validateDraft()))
        return;
      this.saving = true;
      try {
        const res = await globalAxios.put(
          this.endpoint("contracts/" + this.form.contract_id),
          this.payload(recalculate)
        );
        this.form = Object.assign(
          this.emptyForm(),
          this.normalizeResponse(res, {})
        );
        this.form.rounds = this.form.rounds || [];
        this.routePoints = this.extractRoutePoints(this.form);
        this.capturePriceSnapshotInputs();
        this.captureContractInputs();
        await this.fetchContracts();
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถบันทึกสัญญาได้"));
      } finally {
        this.saving = false;
      }
    },
    async setStatus(status) {
      if (this.isDeleted) {
        this.showError("สัญญาที่ลบแล้วเปิดดูได้อย่างเดียว");
        return;
      }
      if (status === "active" && this.priceSnapshotDirty) {
        this.showError(
          "ข้อมูลที่มีผลต่อราคาเปลี่ยนแปลง กรุณาคำนวณราคาใหม่ก่อนเปิดใช้งานสัญญา"
        );
        return;
      }
      if (status === "active" && this.hasUnsavedChanges) {
        this.showError("กรุณาบันทึกการเปลี่ยนแปลงก่อนเปิดใช้งานสัญญา");
        return;
      }
      if (status === "active" && this.hasOverdueRounds) {
        this.showError("กรุณาแก้ไขรอบที่เลยเวลาก่อนเปิดใช้งานสัญญา");
        return;
      }
      this.saving = true;
      try {
        const res = await globalAxios.put(
          this.endpoint("contracts/" + this.form.contract_id),
          {
            status: status,
          }
        );
        this.form = Object.assign(
          this.emptyForm(),
          this.normalizeResponse(res, {})
        );
        this.form.rounds = this.form.rounds || [];
        this.routePoints = this.extractRoutePoints(this.form);
        this.capturePriceSnapshotInputs();
        this.captureContractInputs();
        await this.fetchContracts();
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถอัปเดตสถานะได้"));
      } finally {
        this.saving = false;
      }
    },
    async refreshProgress(contract) {
      if (
        !contract ||
        !contract.contract_id ||
        contract.delete_at ||
        contract.status !== "active"
      )
        return;
      this.saving = true;
      try {
        const res = await globalAxios.post(
          this.endpoint(
            "contracts/" + contract.contract_id + "/refresh_progress"
          )
        );
        const data = this.normalizeResponse(res, {});
        if (this.form.contract_id === contract.contract_id) {
          this.form = Object.assign(this.emptyForm(), data);
          this.form.rounds = this.form.rounds || [];
        }
        await this.fetchContracts();
      } catch (err) {
        this.showError(
          this.apiErrorMessage(err, "ไม่สามารถรีเฟรชความคืบหน้าได้")
        );
      } finally {
        this.saving = false;
      }
    },
    async duplicateContract(contract) {
      if (!contract || !contract.contract_id || contract.delete_at) return;
      this.duplicatingContractId = contract.contract_id;
      try {
        const res = await globalAxios.post(
          this.endpoint("contracts/" + contract.contract_id + "/duplicate")
        );
        const duplicate = this.normalizeResponse(res, {});
        await this.fetchContracts();
        await this.selectContract(duplicate);
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถทำสำเนาสัญญาได้"));
      } finally {
        this.duplicatingContractId = null;
      }
    },
    async createOrderNow(round) {
      if (!round || !round.round_id || this.isDeleted) return;
      this.creatingRoundId = round.round_id;
      try {
        await globalAxios.post(
          this.endpoint(
            "contracts/" +
              this.form.contract_id +
              "/rounds/" +
              round.round_id +
              "/create_order_now"
          )
        );
        await this.refreshProgress(this.form);
      } catch (err) {
        this.showError(
          this.apiErrorMessage(err, "ไม่สามารถสร้างออเดอร์สำหรับรอบนี้ได้")
        );
      } finally {
        this.creatingRoundId = null;
      }
    },
    async deleteContract(contract) {
      if (!contract || !contract.contract_id) return;
      const title =
        "ลบสัญญา " +
        (contract.contract_code || contract.contract_id) +
        " หรือไม่";
      let confirmed = false;
      if (this.$swal) {
        const result = await this.$swal.fire({
          title: title,
          text: "สัญญาจะถูกนำออกจากรายการที่ใช้งานและเก็บไว้ในประวัติการลบ",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "ลบ",
          cancelButtonText: "ยกเลิก",
        });
        confirmed = !!result.value;
      } else {
        confirmed = window.confirm(title);
      }
      if (!confirmed) return;
      this.deletingContractId = contract.contract_id;
      try {
        await globalAxios.delete(
          this.endpoint("contracts/" + contract.contract_id)
        );
        if (this.form.contract_id === contract.contract_id) {
          this.dialog = false;
          this.form = this.emptyForm();
        }
        await this.fetchContracts();
        if (this.deletedDialog) await this.fetchDeletedContracts();
      } catch (err) {
        this.showError(this.apiErrorMessage(err, "ไม่สามารถลบสัญญาได้"));
      } finally {
        this.deletingContractId = null;
      }
    },
    validateDraft() {
      if (this.ownerMode === "customer" && !this.form.owner_user_id) {
        this.showError("กรุณาเลือกลูกค้า");
        return false;
      }
      if (this.ownerMode === "company" && !this.form.owner_company_id) {
        this.showError("กรุณาเลือกบริษัท");
        return false;
      }
      if (this.derivedRoundCount < 1) {
        this.showError("กรุณาเพิ่มอย่างน้อย 1 รอบก่อนสร้างสัญญา");
        return false;
      }
      if (this.derivedRoundCount > 500) {
        this.showError("สัญญามีได้สูงสุด 500 รอบ");
        return false;
      }
      const invalidRound = (this.form.rounds || []).some(
        (round) => !moment(round.scheduled_at, moment.ISO_8601, true).isValid()
      );
      if (invalidRound) {
        this.showError("กรุณาระบุวันและเวลาที่ถูกต้องให้ครบทุกรอบ");
        return false;
      }
      if (this.routePoints.length > 20) {
        this.showError("เส้นทางมีได้สูงสุด 20 จุด");
        return false;
      }
      return true;
    },
    validatePricingInputs() {
      if (!this.validateDraft()) return false;
      if (!this.form.vehicle_id) {
        this.showError("กรุณาเลือกประเภทรถ");
        return false;
      }
      if (this.routePoints.length < 2 || this.routePoints.length > 20) {
        this.showError("เส้นทางต้องมี 2 ถึง 20 จุด");
        return false;
      }
      const invalidStop = this.routePoints.some(
        (stop) => stop.lat === null || stop.lng === null
      );
      if (invalidStop) {
        this.showError("กรุณาระบุละติจูดและลองจิจูดให้ครบทุกจุด");
        return false;
      }
      return true;
    },
    roundLocked(round) {
      return !!(
        round &&
        (round.messenger_order_uuid ||
          round.messenger_order_code ||
          round.status === "order_created" ||
          round.status === "completed")
      );
    },
    canCreateOrder(round) {
      return (
        this.form.status === "active" &&
        !this.isDeleted &&
        round &&
        round.round_id &&
        !this.roundLocked(round) &&
        (round.status === "planned" ||
          round.status === "failed" ||
          round.status === "waiting_for_credit")
      );
    },
    statusColor(status) {
      const map = {
        draft: "grey",
        active: "green",
        orders_created: "teal",
        paused: "orange",
        // completed: "blue",
        completed: "teal",
        expired: "blue-grey",
        cancelled: "red",
        waiting_for_credit: "orange",
      };
      return map[status] || "grey";
    },
    contractStatusLabel(status) {
      const map = {
        draft: "ฉบับร่าง ✏️",
        active: "กำลังใช้งาน 🚀",
        orders_created: "สร้างออเดอร์ครบแล้ว 🎉",
        paused: "พักการใช้งาน ⏸️",
        // completed: "เสร็จสิ้น ✅",
        completed: "สร้างออเดอร์ครบแล้ว 🎉",
        expired: "หมดอายุ ⏰",
        cancelled: "ยกเลิกแล้ว 👋",
      };
      return map[status] || status || "-";
    },
    provinceIdFromSlug(province) {
      const map = {
        phuket: 66,
        suratthani: 67,
        krabi: 64,
        trang: 72,
        songkhla: 70,
        yala: 75,
        prachuapkhirikhan: 62,
        nakhonsithammarat: 63,
        pattani: 74,
        ranong: 68,
        phatthalung: 73,
        satun: 71,
        pathumthani: 4,
        narathiwat: 76,
        hatyai: 78,
        phangnga: 65,
      };
      return map[province] || null;
    },
    selectedProvinceId() {
      const selected = this.provinceOptions.find(
        (item) => item.value === this.province
      );
      return selected ? selected.id : this.provinceIdFromSlug(this.province);
    },
    roundStatusColor(status) {
      const map = {
        planned: "grey",
        order_created: "blue",
        completed: "green",
        failed: "red",
        cancelled: "orange",
        expired: "blue-grey",
        waiting_for_credit: "orange",
      };
      return map[status] || "grey";
    },
    roundStatusLabel(status) {
      const map = {
        planned: "รอสร้างออเดอร์ 🚀",
        order_created: "สร้างออเดอร์แล้ว 🛵",
        completed: "จัดส่งสำเร็จ 🙌",
        waiting_for_credit: "รอตรวจสอบเครดิต ⚠️",
        failed: "สร้างออเดอร์ไม่สำเร็จ 🛠️",
        cancelled: "ยกเลิกแล้ว 👋",
        expired: "เลยกำหนดเวลา ⏰",
      };
      return map[status] || status || "รอสร้างออเดอร์ 🚀";
    },
    creditTransactionTypeLabel(type) {
      const labels = {
        top_up: "เติมเครดิต",
        withdraw: "ถอนเครดิต",
        order_deduction: "หักค่าออเดอร์",
        order_refund: "คืนเครดิตจากออเดอร์",
        opening_balance: "ยอดเครดิตเริ่มต้น",
        bonus_credit: "เครดิตโบนัส",
        manual_adjustment_credit: "ปรับเพิ่มเครดิต",
        manual_adjustment_debit: "ปรับลดเครดิต",
      };
      return labels[type] || type || "-";
    },
    money(value) {
      const numberValue = Number(value || 0);
      return numberValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDateTime(value) {
      if (!value) return "";
      const date = moment(value);
      if (!date.isValid()) return value;
      return date.format("YYYY-MM-DD HH:mm");
    },
    showError(text) {
      if (this.$swal) {
        this.$swal.fire("เกิดข้อผิดพลาด", text, "error");
      } else {
        alert(text);
      }
    },
    apiErrorMessage(error, fallback) {
      const response = error && error.response;
      const data = response && response.data;
      const detail = data && (data.detail || data.message);
      const messages = {
        "permission denied": "คุณไม่มีสิทธิ์ทำรายการนี้",
        "company not found": "ไม่พบบริษัท",
        "customer not found": "ไม่พบลูกค้า",
        "insufficient available balance": "ยอดเครดิตที่ใช้ได้ไม่เพียงพอ",
      };
      return messages[detail] || fallback;
    },
  },
};
</script>

<style scoped>
.messenger-contract-page {
  min-height: 100%;
  padding: 24px 16px 40px;
  position: relative;
}

.messenger-contract-page--embedded {
  min-height: auto;
  padding: 0px;
}

.contract-hero {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto 22px;
  max-width: 1400px;
  padding: 18px 10px;
  position: relative;
}

.hero-eyebrow {
  color: #9b8500;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
}

.hero-title {
  color: #263238;
  font-size: 42px;
  font-weight: 750;
  letter-spacing: -0.04em;
  line-height: 1.1;
  margin: 7px 0;
  text-shadow: 2px 3px 12px rgba(33, 33, 32, 0.25);
}

.hero-subtitle {
  color: #73808a;
  font-size: 16px;
  margin: 0;
}

.hero-icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(44, 62, 80, 0.11);
  display: flex;
  height: 82px;
  justify-content: center;
  width: 82px;
}

.hero-icon .v-icon {
  color: #d6b900;
  font-size: 38px;
}

.contract-shell,
.section-card {
  border-radius: 16px;
}

.contract-shell--embedded,
.section-card--embedded {
  border-radius: 0px;
}

.contract-shell {
  margin: 0 auto;
  max-width: 1400px;
  overflow: hidden;
  position: relative;
  padding-top: 20px;
}

.contract-shell--embedded {
  margin: 0;
  padding-top: 20px;
  max-width: 100%;
}

.glass-card,
.glass-section {
  background: rgba(255, 255, 255, 0.68) !important;
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  box-shadow: 0 18px 52px rgba(27, 39, 51, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.contract-toolbar {
  min-height: 88px;
}

.toolbar-copy {
  min-width: 170px;
}

.toolbar-title {
  color: #263238;
  font-size: 20px;
  font-weight: 700;
}

.toolbar-subtitle {
  color: #82909a;
  font-size: 12px;
  margin-top: 3px;
}

.province-filter {
  margin-right: 12px;
  max-width: 230px;
}

.status-filter {
  max-width: 160px;
  margin-right: 12px;
}

.summary-row {
  padding: 8px 16px 16px;
}

.summary-item {
  align-items: center;
  background: rgba(255, 255, 255, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(35, 45, 55, 0.06);
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 14px;
}

.summary-value {
  color: #222222;
  font-size: 22px;
  font-weight: 700;
}

.summary-label,
.muted-text {
  color: #777777;
  font-size: 12px;
}

.strong-text {
  color: #222222;
  font-weight: 600;
}

.contract-table {
  border-top: 1px solid #eeeeee;
}

.contract-table >>> .v-table__overflow,
.deleted-contract-table >>> .v-table__overflow,
.round-table >>> .v-table__overflow {
  overflow-x: auto;
}

.contract-table >>> table {
  min-width: 960px;
}

.deleted-contract-table >>> table {
  min-width: 900px;
}

.round-table >>> table {
  min-width: 1180px;
}

.round-table >>> th:nth-child(2),
.round-table >>> td:nth-child(2) {
  min-width: 360px;
  width: 360px;
}

.round-schedule-picker {
  min-width: 330px;
}

.round-summary-fields {
  background: rgba(247, 249, 250, 0.82);
  border: 1px solid #e3e8eb;
  border-radius: 12px;
  margin-bottom: 12px;
  padding: 6px 10px 0;
}

.section-card {
  margin-bottom: 16px;
}

.dialog-glass-card {
  background: rgba(244, 247, 248, 0.84) !important;
  border: 1px solid rgba(255, 255, 255, 0.78) !important;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-toolbar {
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.contract-form-btn {
  padding: 0% 16px;
  font-weight: 700;
  letter-spacing: 0;
  min-height: 36px;
  text-transform: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.contract-form-btn:not(.v-btn--disabled):hover {
  box-shadow: 0 8px 20px rgba(38, 50, 56, 0.2);
  transform: translateY(-1px);
}

.contract-status-actions {
  display: flex;
  gap: 8px;
  margin-top: 0;
}

.contract-calculate-btn {
  margin-bottom: 16px;
}

.contract-draft-btn {
  margin-top: 0;
  margin-bottom: 20px;
}

.contract-status-actions .v-btn {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding-left: 8px;
  padding-right: 8px;
}

.company-credit-summary {
  display: flex;
  align-items: center;
  width: 100%;
}

.company-credit-summary__text {
  flex: 1 1 auto;
  min-width: 0;
}

.company-credit-summary__refresh {
  flex: 0 0 auto;
  margin: -6px -6px -6px 8px;
}

.dialog-body {
  max-width: 1500px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.sticky-panel {
  position: sticky;
  top: 12px;
}

.price-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;
}

.price-text {
  color: #1b5e20;
  font-size: 24px;
  font-weight: 700;
}

.snapshot-empty-state {
  border: 1px dashed #b0bec5;
  border-radius: 8px;
  padding: 24px 16px;
  text-align: center;
}

.snapshot-empty-state__title {
  font-size: 16px;
  font-weight: 600;
  margin: 8px 0 4px;
}

.progress-line {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
}

.route-builder {
  align-items: center;
  background: #f7f9fa;
  border: 1px solid #dfe5e8;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(280px, 1fr) auto auto;
  margin-bottom: 14px;
  padding: 12px;
}

.route-builder .v-btn {
  margin: 0;
}

.route-requirement {
  margin-bottom: 14px;
}

.route-stop-name {
  align-items: flex-start;
  display: flex;
  gap: 8px;
}

.route-table >>> table {
  min-width: 1320px;
  table-layout: fixed;
}

.route-stop-list {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.route-stop-card {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(210, 217, 222, 0.9);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.06);
  padding: 16px;
}

.route-stop-card__header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: space-between;
  min-height: 36px;
}

@media (max-width: 960px) {
  .route-stop-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .route-builder {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .route-builder > :first-child {
    grid-column: 1 / -1;
  }

  .contract-toolbar {
    height: auto !important;
  }

  .contract-toolbar >>> .v-toolbar__content {
    align-items: stretch;
    flex-wrap: wrap;
    height: auto !important;
    padding-bottom: 14px;
    padding-top: 14px;
  }

  .contract-toolbar >>> .v-toolbar__content > .spacer {
    display: none;
  }

  .toolbar-copy {
    flex: 1 0 100%;
    margin-bottom: 12px;
  }

  .province-filter,
  .status-filter {
    max-width: none;
  }

  .sticky-panel {
    position: static;
  }

  .section-card > .v-card__title {
    flex-wrap: wrap;
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .route-builder {
    grid-template-columns: minmax(0, 1fr);
  }

  .route-builder > :first-child {
    grid-column: auto;
  }

  .route-builder__add,
  .route-builder__custom {
    width: 100%;
  }

  .messenger-contract-page {
    padding: 12px 6px 28px;
  }

  .contract-hero {
    margin-bottom: 10px;
    padding: 10px 6px;
  }

  .hero-title {
    font-size: 30px;
  }

  .hero-icon {
    display: none;
  }

  .contract-shell,
  .glass-card {
    border-radius: 12px;
  }

  .contract-shell {
    padding-top: 6px;
  }

  .contract-toolbar >>> .v-toolbar__content {
    gap: 0 6px;
    padding: 10px;
  }

  .toolbar-copy {
    margin-bottom: 10px;
  }

  .contract-toolbar .toolbar-icon-btn {
    margin: 0 2px;
    min-width: 42px;
  }

  .contract-toolbar .province-filter,
  .contract-toolbar .status-filter {
    flex: 1 0 calc(50% - 3px);
    margin: 0 0 10px;
    min-width: 0;
    width: calc(50% - 3px);
  }

  .contract-toolbar .create-contract-btn {
    flex: 1 0 100%;
    margin: 8px 0 0;
    width: 100%;
  }

  .summary-row {
    padding-bottom: 10px;
    padding-left: 8px;
    padding-right: 8px;
  }

  .summary-item {
    gap: 8px;
    min-height: 66px;
    padding: 10px;
  }

  .summary-value {
    font-size: 19px;
  }

  .contract-table {
    background: transparent;
    border-top: 0;
  }

  .contract-table >>> .v-table__overflow {
    overflow-x: visible;
  }

  .contract-table >>> table {
    background: transparent;
    min-width: 0;
  }

  .contract-table >>> thead {
    display: none;
  }

  .contract-table >>> tbody {
    display: block;
    padding: 0 8px 8px;
  }

  .contract-table >>> tbody tr {
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid #e3e8eb;
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(38, 50, 56, 0.07);
    display: block;
    margin-bottom: 10px;
    overflow: hidden;
  }

  .contract-table >>> tbody td {
    align-items: center;
    border-bottom: 1px solid #edf0f2;
    display: grid !important;
    grid-template-columns: 108px minmax(0, 1fr);
    height: auto !important;
    min-height: 44px;
    padding: 8px 12px !important;
    text-align: left !important;
    white-space: normal;
  }

  .contract-table >>> tbody td::before {
    color: #7b878e;
    content: attr(data-label);
    font-size: 11px;
    font-weight: 700;
    padding-right: 10px;
  }

  .contract-table >>> tbody td:last-child {
    border-bottom: 0;
  }

  .contract-table >>> tbody td > * {
    min-width: 0;
  }

  .contract-table >>> tbody .contract-row-actions {
    display: flex !important;
    justify-content: flex-start !important;
  }

  .contract-table >>> tbody .contract-row-actions::before {
    flex: 0 0 108px;
  }

  .contract-table >>> tbody .contract-row-actions .v-btn {
    margin: 0 2px;
  }

  .contract-table >>> tbody td[colspan] {
    display: block !important;
    padding: 0 !important;
  }

  .contract-table >>> tbody td[colspan]::before {
    display: none;
  }

  .contract-table >>> .v-datatable__actions {
    flex-wrap: wrap;
    height: auto;
    justify-content: center;
    min-height: 56px;
    padding: 8px;
  }

  .price-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 380px) {
  .contract-toolbar .province-filter,
  .contract-toolbar .status-filter {
    flex-basis: 100%;
    width: 100%;
  }

  .summary-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .contract-table >>> tbody td {
    grid-template-columns: 92px minmax(0, 1fr);
  }

  .contract-table >>> tbody .contract-row-actions::before {
    flex-basis: 92px;
  }
}
</style>

<style>
.contract-dialog,
.deleted-contract-dialog {
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(15, 23, 32, 0.28);
  overflow: hidden;
}

.contract-detail-card,
.deleted-contract-card {
  max-height: calc(100vh - 48px);
}

.contract-detail-card .contract-dialog-content {
  max-height: calc(100vh - 112px);
  overflow-y: auto;
  padding: 0;
}

.deleted-contract-card .v-card__text {
  max-height: calc(100vh - 130px);
  overflow-y: auto;
}

.messenger-contract-page .v-btn,
.contract-dialog .v-btn,
.deleted-contract-dialog .v-btn {
  --contract-button-shadow: rgba(38, 50, 56, 0.22);
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.messenger-contract-page .v-btn.primary,
.messenger-contract-page .v-btn.primary--text,
.contract-dialog .v-btn.primary,
.contract-dialog .v-btn.primary--text,
.deleted-contract-dialog .v-btn.primary,
.deleted-contract-dialog .v-btn.primary--text {
  --contract-button-shadow: rgba(255, 193, 7, 0.38);
}

.messenger-contract-page .v-btn.success,
.messenger-contract-page .v-btn.success--text,
.contract-dialog .v-btn.success,
.contract-dialog .v-btn.success--text,
.deleted-contract-dialog .v-btn.success,
.deleted-contract-dialog .v-btn.success--text {
  --contract-button-shadow: rgba(76, 175, 80, 0.36);
}

.messenger-contract-page .v-btn.warning,
.messenger-contract-page .v-btn.warning--text,
.contract-dialog .v-btn.warning,
.contract-dialog .v-btn.warning--text,
.deleted-contract-dialog .v-btn.warning,
.deleted-contract-dialog .v-btn.warning--text {
  --contract-button-shadow: rgba(255, 152, 0, 0.38);
}

.messenger-contract-page .v-btn.error,
.messenger-contract-page .v-btn.error--text,
.contract-dialog .v-btn.error,
.contract-dialog .v-btn.error--text,
.deleted-contract-dialog .v-btn.error,
.deleted-contract-dialog .v-btn.error--text {
  --contract-button-shadow: rgba(255, 82, 82, 0.38);
}

.messenger-contract-page .v-btn.blue-grey,
.messenger-contract-page .v-btn.blue-grey--text,
.contract-dialog .v-btn.blue-grey,
.contract-dialog .v-btn.blue-grey--text,
.deleted-contract-dialog .v-btn.blue-grey,
.deleted-contract-dialog .v-btn.blue-grey--text {
  --contract-button-shadow: rgba(96, 125, 139, 0.34);
}

.messenger-contract-page .v-btn:not(.v-btn--icon),
.contract-dialog .v-btn:not(.v-btn--icon),
.deleted-contract-dialog .v-btn:not(.v-btn--icon) {
  border-radius: 24px;
  min-width: 104px;
  padding-left: 24px;
  padding-right: 24px;
  box-shadow: 0 8px 18px var(--contract-button-shadow) !important;
  transform: translateY(-2px);
}

.messenger-contract-page .v-btn--icon,
.contract-dialog .v-btn--icon,
.deleted-contract-dialog .v-btn--icon {
  box-shadow: 0 6px 14px var(--contract-button-shadow) !important;
  transform: translateY(-1px);
}

.messenger-contract-page .v-btn.v-btn--disabled,
.contract-dialog .v-btn.v-btn--disabled,
.deleted-contract-dialog .v-btn.v-btn--disabled {
  box-shadow: none !important;
  transform: none;
}

.messenger-contract-page .v-btn:not(.v-btn--disabled):hover,
.contract-dialog .v-btn:not(.v-btn--disabled):hover,
.deleted-contract-dialog .v-btn:not(.v-btn--disabled):hover {
  box-shadow: 0 10px 24px var(--contract-button-shadow) !important;
  transform: translateY(-3px);
}

@media (max-width: 600px) {
  .contract-dialog,
  .deleted-contract-dialog {
    border-radius: 0;
    height: 100vh;
    height: 100dvh;
    margin: 0;
    max-height: none;
    max-width: none;
    width: 100%;
  }

  .contract-detail-card,
  .deleted-contract-card {
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    max-height: none;
  }

  .contract-detail-card .contract-dialog-content,
  .deleted-contract-card .v-card__text {
    flex: 1 1 auto;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
  }

  .dialog-toolbar {
    height: auto !important;
  }

  .dialog-toolbar .v-toolbar__content {
    flex-wrap: wrap;
    height: auto !important;
    min-height: 108px;
    padding: 10px 12px;
  }

  .dialog-toolbar .v-toolbar__title {
    flex: 1 0 calc(100% - 48px);
    font-size: 16px;
    line-height: 1.3;
    overflow: visible;
    white-space: normal;
  }

  .dialog-toolbar .contract-form-btn {
    margin-left: 0;
  }
}
</style>
